// The home's money flow (v1.2–v1.3): the keypad home, the Sending card, Get paid.
//
//   Sending   amount (money pills + keypad, additive) → Send $X → ONE card:
//             nobody chosen = M® Note (a note by link, locked with a code by
//             default); a contact chosen from My M® contacts = a payment to
//             them (Slide to confirm). No To field, no typed names: a new
//             person is reached by their link or QR, which open the Paying page.
//   Get paid  the header's toggle: your QR → the amount → the request (QR,
//             link, note, Share).
//
// v1.3.1 — the Drop paths that lived here (a first payment to someone new as
// a bearer Drop with the rest held back; the v0.70 escrow Drop) are gone: no
// screen has led to them since 1.1.21, when M® Note took their place. Drops
// made before then are still claimed, confirmed, shared again and cancelled
// from their own pages and from Balance → Pending (dropClaimView,
// dropConfirmView, dropCatchView) — nothing there changed.
//
// A reference differs by direction: on a request it is part of the public
// link; on a payment it is an M-side note (USDC carries no memo) that reaches
// the statement through /tx/record.

import { SPARE_ACT, SPARE_LINE } from "./sparePrompt";
import { playSent, preloadSent, unlockSound } from "./sound";
import { brandedQrSvg } from "./qr";
import { canonicalizeCode, isWalletCode, WALLET_CODE_LEN } from "./codes";
import type { WalletAdapter } from "./adapters/WalletAdapter";
import type { WalletName } from "./api";
import { recordTx, resolveName } from "./api";
import { encodeFunctionData, getAddress, isAddress, parseUnits } from "viem";
import { LOGO_M } from "./logo";
import {
  MAX_AMOUNT,
  canonicalAmount,
  deslugifyRef,
  formatUsd,
  formatUsdShort,
  requestUrl,
  slugifyRef,
} from "./money";
import { addRecent, readRecent } from "./recentStore";
import { alsoNames, contactLabel, listNicks } from "./nickname";
import { cachedProfile } from "./profileCache";
import { emojiFor } from "./emoji";
import { warmProfiles } from "./profileCache";
import { addTxLog } from "./txLog";
import { ARROW_R, IC_COPY, IC_LOCK_M, IC_CHEVRON_DOWN, IC_RELEASE_DELAY, IC_SCAN_M, IC_SWAP_UD, IC_TIMELOCK, TICK_CIRCLE, TICK_CIRCLE_FILL } from "./icons";
import { cancelThis, holdMoney, holdThis, onTimelock, paidThis, phaseOf, release as tlRelease, tlNow, tlPrimary, tlView, whenShort, whenText, wouldHold } from "./timelock"; // 1.5.59
import type { TimelockHold } from "./api";
import { failureLine, fitFailTitle, isPasskeyCancel, PASSKEY_CANCEL_LINE, recordError, recordMeasure } from "./diag";
import type { PreparedOp, Submitted } from "./adapters/WalletAdapter"; // v0.90.26 / .29
import { backingLine, currency, formatFx, liveCurrencies, pocketRow, setPocketSlot, setSessionCurrency, type FxSource, type FxTopUp } from "./fx";
import { shareOrCopy } from "./share";
import { initSlideToConfirm } from "./slideToConfirm";
// v1.1.15 — M® Note. Only the light modules here: notes.ts re-exports derive()
// from kdf.ts, which starts the KDF in a Web Worker; lockcode.ts (the wasm
// argon2id) is never imported by a screen, so it stays out of the main bundle.
import {
  noteSource, newSecret, kdfInput, derive, keepSent, patchSent, forgetSent, settleSent, noteLink, listSent, reconcileSent, ZERO_SALT, feeUnits,
  type Call, type NoteFeeCalls, type NewSecret, type SecretKind, type Derived, type NoteView, type SentNote,
} from "./notes/notes";
import { formatCode } from "./notes/code";
import { formatSerial } from "./notes/serial";
import { recordNote, issuingEnabled, noteFee, type NoteFee } from "./notes/noteApi";
import { PREPARED_TTL_MS, sponsorOnTouch } from "./adapters/WalletAdapter";
import { formatNoteAmount, moneyWord, noteCurrencyBlocked, sentCurrency } from "./noteCurrency";
import { LINK_ORIGIN } from "./linkOrigin";
import { watchAmounts } from "./noteAmt"; // 1.4.7: the amount fitted to the face
import { BC_GLYPH, dismiss as dismissBroadcast, isDismissed, liveBroadcasts, pick as pickBroadcast } from "./broadcast"; // 1.4.87
import type { Broadcast } from "../../shared/api";

// v0.18.4 — Send/Receive are the two diagonal arrows flanking the amount,
// mirroring SF Symbols arrow.up.right (send) and arrow.down.left (receive).
// v1.1.21 — the keypad's delete, the card's scan squircle and the list's search.
const IC_DEL = `<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.7" stroke-linejoin="round" stroke-linecap="round" aria-hidden="true"><path d="M9 5h10.5A1.5 1.5 0 0 1 21 6.5v11a1.5 1.5 0 0 1-1.5 1.5H9l-6-7z"/><path d="M12 9.5l5 5"/><path d="M17 9.5l-5 5"/></svg>`;
const IC_FIND = `<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" aria-hidden="true"><circle cx="11" cy="11" r="6.5"/><path d="M16 16l4 4"/></svg>`;
// v1.1.12 — M® Note's lock row (round-3 glyphs): the checkbox's tick, and the
// padlock that stands in for the box from $50, where the lock is not a choice.
const IC_LOCK_TICK = `<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.6" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true"><path d="M5 12.5l4.5 4.5L19 7.5"/></svg>`;
/** M® Note: notes are whole dollars, and from this value a note is always locked. */
const CASH_ALWAYS_LOCKED = 50;
type Mode = "send" | "receive" | "invest"; // v1.1.21 — no selector: M® Note or a contact is decided on the card; 1.4.94 — Invest (Members only) from the dock's pill
// Straight arrow = "proceed / approve" (slider thumb + drop-2 continue controls).
// v1.0.13 — the ✕ that cancels a row's screen (the ✓ moved into the field in v1.1.0: TICK_CIRCLE*).
const IC_CLOSE = `<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.2" stroke-linecap="round" aria-hidden="true"><path d="M6 6l12 12M18 6L6 18"/></svg>`;

// v0.90.19 — a plain send as a call, so it can share a userOp with a top-up.
const ERC20_TRANSFER_ABI = [
  {
    type: "function",
    name: "transfer",
    stateMutability: "nonpayable",
    inputs: [
      { name: "to", type: "address" },
      { name: "amount", type: "uint256" },
    ],
    outputs: [{ type: "bool" }],
  },
] as const;
function escapeHtml(s: string) {
  return s.replace(
    /[&<>"']/g,
    (c) =>
      ({
        "&": "&amp;",
        "<": "&lt;",
        ">": "&gt;",
        '"': "&quot;",
        "'": "&#39;",
      })[c] as string,
  );
}

/** Pre-seed the Send flow — e.g. when arriving from a pay-link's "Go to my
 *  wallet". Recipient is always set; if an amount is given the flow opens straight
 *  on the Send details step ready to slide. */
export interface SendPrefill {
  toAddress: string;
  toDisplay: string;
  amount?: number;
  reference?: string;
}

export interface SendReceiveDeps {
  adapter: WalletAdapter;
  address: string;
  name: WalletName | null;
  /** Current available USDC balance (number) — shown as the Send hero reference. */
  getAvailable: () => number;
  /**
   * v0.80.28 — which pocket Send spends from. Defaults to USD. This is the
   * pocket set as default in the Balance panel (press-and-hold), so the home
   * screen works in one currency at a time rather than growing a picker.
   */
  getCurrency?: () => string;
  /** Total money in M (cash + growing). Drives the Send/Request default + Send muting. */
  getTotal?: () => number;
  /** Open the Add-money sheet (the muted Send's + nudge). */
  onAddMoney?: () => void;
  /** Called when a payment or a note has been accepted, so the home balance can refresh. (The name is from the Drops era.) */
  onDropComplete: () => void;
  /**
   * v0.80.37 — a digit was refused because the amount would exceed the pocket
   * (true), or the amount is back within it (false). The home turns the
   * balance pill alert-coloured and shows Add while it's true.
   */
  onOver?: (over: boolean) => void;
  /** Signals when the user is mid-action (amount entered / in details) so the
   *  home can shrink the nucleus and free vertical space. */
  onActivity?: (active: boolean) => void;
  /** v0.60.18 (S8) — true while a card (Sending / Requesting) is on screen: the host fades the strip + dock. */
  onCard?: (on: boolean) => void;
  /** v0.70.16 — the To field has focus: lift the block to the top (both browsers). */
  onLift?: (on: boolean) => void;
  /** Optional: open the Send flow pre-filled (from a pay link). */
  prefill?: SendPrefill;
  /** v1.1.21 — the name the conversation greets ("Hi Alex."); empty = "Hi." */
  greetName?: () => string;
  /** v1.1.21 — the home switched between sending and getting paid (the header toggle lit, until 1.4.93). 1.4.94 — Invest too; the dock's menu ticks it. */
  onMode?: (mode: "send" | "receive" | "invest") => void;
  /** 1.4.94 (Alexander, 24 Oct) — the dock's split pill, in the view's markup: the word button (#srNext), its label
   *  (#srGoLbl) and the segment (#srX: ✕ with an amount, ⌄ at rest). The flow paints them; the view wires their taps. */
  dockGo: { btn: HTMLButtonElement; x: HTMLButtonElement; lbl: HTMLElement };
  /** v1.1.21 — the card's scan squircle: open the scanner. (Get paid's Scan M® QR went in 1.4.91.) */
  onScan?: () => void;
  /** v1.1.21 — Get paid at $0: show my QR (today's Get paid popup, until 1.1.22). */
  onShowQr?: () => void;
  /** v1.1.25 — Global: a pocket pill was tapped (it becomes the active pocket). */
  onPocket?: (code: string) => void;
  /** v1.1.30 — Global: pill 2 or 3 was tapped: the list of your pockets; `pick` sets the pill. */
  onPocketList?: (code: string, pick: (code: string) => void) => void;
  /** v1.1.25 — Global: the Swap squircle. */
  onSwap?: () => void;
  /** v1.1.27 — "Check my money" opened Activity; v1.3.1 — the opening line's "doing well." does. */
  onCheckMoney?: () => void;
  /** 1.5.45 (HL-17) — the opening line is "Protect your wallet. Make a Spare Key." now (sparePrompt.ts). */
  sparePrompt?: () => boolean;
  /** 1.5.45 — the prompt was painted (once a day from there). */
  onSparePromptShown?: () => void;
  /** 1.5.45 — "Make a Spare Key." was tapped: the Spare Key screen. */
  onSparePrompt?: () => void;
  /**
   * v1.3.0 — is there money in this wallet? true: yes (read, or the last known
   * state); false: read, and nothing; null: not read yet. The opening line
   * waits for an answer rather than guess ("Your money's doing well." is never
   * said to an empty wallet). Absent → the total decides.
   */
  funded?: () => boolean | null;
  /**
   * v1.3.0 — the welcome money of a wallet just made with an invite: the
   * amount to say, "wait" while it is on its way and no amount is known yet,
   * null otherwise. v1.3.1 — the amount is the one the Worker said it is
   * sending (known at once), or, from an older Worker, what arrived.
   */
  welcomeMoney?: () => number | "wait" | null;
  /** 1.4.57 — a wallet made on a note's take page: the note's amount as the face prints it ("€1.41"), said once. */
  welcomeNote?: () => string | null;
  /**
   * v0.90.19 — the ladder of sources. A payment names an amount and a currency;
   * when the pocket can't cover it, rung 3 converts the SHORTFALL from another
   * pocket inside the same userOp (default pocket first, then largest), disclosed
   * on the card's SOURCE: row and approved by the existing slide. Absent → the
   * ladder has no rung 3 and the pocket is the ceiling, as before.
   */
  fx?: FxSource;
  ladder?: { pockets: () => Record<string, number>; defaultPocket: () => string };
}

export interface SendReceiveHandle {
  /** Re-read the available balance into the Send hero reference line. */
  refreshAvailable: () => void;
  /** Return to the amount/keypad screen from a details step (value preserved). */
  backToAmount: () => void;
  /** Back to the keypad with the amount cleared to $0 (the dock Send/Request tap). */
  home: () => void;
  /** Open the Send flow pre-filled at runtime (e.g. from a scanned code). */
  openSend: (prefill: SendPrefill) => void;
  /** 1.5.59 — the Timelock: the held card for the hold the home shows (the balance pill's tap while one is open). */
  openHold: () => void;
  /** Show the amount step fresh (direction defaults by balance: Send when funded, Request when empty). */
  open: () => void;
  /** v1.1.21 — the header's Get paid toggle: ask instead of send (or back). 1.4.94 — the dock's pill menu. */
  setMode: (mode: "send" | "receive" | "invest") => void;
  /** 1.4.94 — the dock pill's two taps: the word (Next with an amount), the segment (✕ with an amount). */
  go: () => void;
  clear: () => void;
  /** 1.4.94 — the mode the pill shows (the menu ticks it). */
  mode: () => "send" | "receive" | "invest";
  /** v1.1.21 — repaint the conversation line (a balance read, the greeting's name). */
  repaint: () => void;
  /** v1.1.25 — the globe: on = the pockets row in place of the money pills. */
  setGlobal: (on: boolean) => void;
}

export function renderSendReceive(
  container: HTMLElement,
  deps: SendReceiveDeps,
): SendReceiveHandle {
  const { adapter, address, name } = deps;
  watchAmounts(); // 1.4.7 — the sent card's note: its amount fitted to the face
  // v0.80.28 — the pocket Send spends from (the Balance panel's default).
  const cur = () => deps.getCurrency?.() ?? "USD";
  /** Short amount in the pocket's own currency (`$25`, `€22.33`). */
  const fmt = (n: number) =>
    cur() === "USD" ? formatUsdShort(n) : formatFx(cur(), n);

  container.innerHTML = `
    <section class="sr">
      <!-- v1.1.21 — the home redesign (decided 3–4 Oct, mock-ups final 4 Oct
           16:17). No selector: the home sends by default; the header's Get
           paid toggle asks instead. The conversation space (greeting + the
           question, or one message line with its answer chips) sits over
           the money pills and the keypad; the bottom row is ✕ + Send $X →. -->
      <div id="srAmount" class="h21">
        <!-- 1.4.87 — a critical message from M: the banner under the top bar; no ✕, it stays until M ends it -->
        <section class="bc21" id="srBcBanner" role="alert" hidden></section>
        <div class="say21" id="srSay">
          <!-- 1.4.87 — an info or warning message from M, in the home's lines' place until dismissed -->
          <section class="bc21" id="srBcPanel" role="status" hidden></section>
          <p class="s-greet" id="srGreet"></p>
          <p class="s-line" id="srLine" hidden></p>
          <div class="s-amt" id="srAmtWrap" hidden><span class="numw"><span class="cur" id="srCur">$</span><span class="num" id="srAmt">0</span></span></div>
          <p class="s-ask" id="srAsk"></p>
          <div class="s-chips" id="srChips" hidden></div>
        </div>
        <div class="pills21 rest" id="srPills" aria-label="Add to the amount">${[5, 10, 20, 50].map((v) => `<button type="button" class="k21" data-v="${v}"><span class="pcur">$</span>${v}</button>`).join("")}</div>
        <!-- v1.1.25 — the lit globe: the money pills become your pockets, with Swap at the end -->
        <div class="pk21" id="srPockets" hidden></div>
        <div class="kp21 rest" id="srPad">
          ${["1", "2", "3", "4", "5", "6", "7", "8", "9"].map((k) => `<button type="button" class="k21" data-k="${k}">${k}</button>`).join("")}
          <button type="button" class="k21 bare" data-k="." aria-label="Point">·</button><button type="button" class="k21" data-k="0">0</button><button type="button" class="k21 bare" data-k="back" aria-label="Delete">${IC_DEL}</button>
        </div>
        <!-- 1.4.94 (Alexander, 24 Oct) — the Send row is gone: Send/Next and ✕ live in the dock's split pill (the view's
             #srNext / #srX, reached through deps.dockGo), the scan squircle in the header's top right. The row's slot
             stays (visibility), so the keypad doesn't move; in Get paid it holds Show my QR. -->
        <div class="act21">
          <button id="srGpShow" class="gpshow21" type="button">Show my QR</button>
        </div>
      </div>

      <!-- STEP 2 — the Sending card (v1.1.21): one card for M® Note and people. -->
      <div id="srDetails" hidden>
        <div id="srSend">
          <div class="paycard sr-card c21" id="srCard">
            <button class="pc-x" id="scClose" type="button" aria-label="Close">${IC_CLOSE}</button>
            <div class="pc-title">Sending</div>
            <button class="c21-amt" id="pcAmtRow" type="button" aria-label="Change the amount"><b class="pc-amt amt21" id="pcAmt"></b></button>
            <!-- 1.5.3 (Alexander, 24 Oct; canvas R14) — the note path first: the line, Lock and the fee; then "or" (Get paid's)
                 and My M® contacts. A contact picked, or an amount that can't be a note, hides the line, Lock and "or" together. -->
            <p class="c21-say" id="srNoteSay" hidden>Send it to anyone, anywhere</p>
            <div class="c21-go c21-lockwrap">
              <div class="cash-lock c21-lock" id="srLock" hidden>
                <button class="cash-lock-box" id="srLockBox" type="button" role="checkbox" aria-checked="true" aria-labelledby="srLockT">${IC_LOCK_TICK}</button>
                <span class="cash-lock-ic" id="srLockIc" aria-hidden="true">${IC_LOCK_M}</span>
                <div class="cash-lock-txt"><div class="t" id="srLockT">Lock it with a code</div><div class="s" id="srLockS"></div></div>
              </div>
            </div>
            <div class="c21-foot" id="srCashNote" hidden><p id="srCashFee"></p><button type="button" id="srCashNoteNever">Don't show again</button></div>
            <div class="gp-or c21-or" id="srNoteOr" aria-hidden="true" hidden><span>or</span></div>
            <div class="c21-who" id="srWho">
              <button class="c21-scan" id="srScan" type="button" aria-label="Scan their M® QR">${IC_SCAN_M}</button>
              <button class="c21-mine" id="srMine" type="button">My M® contacts<span class="chev" aria-hidden="true">${IC_CHEVRON_DOWN}</span></button>
            </div>
            <div class="c21-chosen" id="srPicked" hidden><span class="nm" id="pcTo"></span><button class="c21-unpick" id="srUnpick" type="button" aria-label="Not this person: back to an M® Note">${IC_CLOSE}</button></div>
            <label class="c21-reff" id="pcRefField" hidden><span class="k">Ref:</span><input id="srRef" maxlength="40" autocomplete="off" enterkeyhint="done" placeholder="Add a note (optional)" aria-label="Reference (optional)" /></label>
            <div class="pc-row pc-row-src" id="pcSrcRow" hidden><span class="pc-k">Source:</span><span class="pc-v" id="pcSrc"></span></div>
          </div>
          <!-- v1.1.30 — the slider on a panel of its own (as on Paying), and the
               M® Note lock row on it, under the slider: the tick box centred
               under the thumb. On by default below $50 and never remembered;
               from $50 a statement, not a choice. -->
          <div class="paycard c21 c21-go" id="srGo">
            <div class="slide" id="srSlide" role="button" aria-label="Send money to anyone">
              <div class="fill"></div>
              <div class="lbl" id="srSlideLbl">Send money to anyone</div>
              <div class="ghost">${ARROW_R}</div>
              <div class="thumb">${ARROW_R}</div>
            </div>
          </div>
          <p id="srStatus" class="status" role="status" aria-live="polite"></p>
          <div class="c21-veil" id="srPeopleVeil" hidden></div>
          <div class="c21-list" id="srPeople" role="dialog" aria-label="My M® contacts" hidden>
            <div class="lh"><button type="button" class="lx" id="srPeopleX" aria-label="Close">${IC_CLOSE}</button>My M® contacts</div>
            <!-- v1.3.3 — the top line: a wallet that isn't a contact yet, by its
                 0x address or its M wallet code. The field's own ✕ and tick. -->
            <div class="sr-edit-field c21-paste" id="srPasteWrap">
              <input id="srPaste" type="text" autocomplete="off" autocapitalize="off" autocorrect="off" spellcheck="false" enterkeyhint="done" placeholder="Paste wallet address" aria-label="Paste a wallet address or an M wallet code" />
              <button type="button" class="sr-edit-clr" id="srPasteClr" aria-label="Clear" tabindex="-1">${IC_CLOSE}</button>
              <button type="button" class="sr-edit-done" id="srPasteDone" aria-label="Use this wallet" tabindex="-1"><span class="o">${TICK_CIRCLE}</span><span class="f">${TICK_CIRCLE_FILL}</span></button>
            </div>
            <p class="c21-paste-msg" id="srPasteMsg" role="status" aria-live="polite" hidden></p>
            <label class="c21-search" id="srFindWrap" hidden>${IC_FIND}<input id="srFind" type="text" autocomplete="off" enterkeyhint="search" placeholder="Find in your contacts" aria-label="Find in your contacts" /></label>
            <div class="rows" id="srPeopleRows"></div>
          </div>
        </div>

      </div>

      <!-- v1.1.23 — Get paid (the header toggle). Three screens: your QR with
           Enter the amount (1.4.91: Scan M® QR gone); the amount on the home's keypad, where the home's
           sits (1.4.91: the small QR above it gone); then the request: its QR, the link, the amount, a
           note and Share. The QR starts at the same top on all three. -->
      <div id="srGp" class="gp21" hidden>
        <div class="gp-qr" id="srGpQr"></div>
        <p class="gp-show"><span class="lt">Show this to get paid</span></p>
        <!-- 1.4.92 (Alexander, 23 Oct) — the copy glyph gone; a faint "or" line introduces the other way to get paid -->
        <div class="gp-or" aria-hidden="true"><span>or</span></div>
        <button type="button" class="gp-pill amt" id="srGpAmt">Enter the amount</button>
        <p class="gp-status" id="srGpSaid" role="status" aria-live="polite"></p>
      </div>
      <div id="srGpLink" class="gp21 link" hidden>
        <div class="gp-qr green" id="srGpReqQr"></div>
        <div class="gp-link"><span class="lt"><span id="srGpLinkText"></span><button type="button" class="gp-copy" id="srGpCopy" aria-label="Copy the link">${IC_COPY}</button></span></div>
        <button type="button" class="gp-amt amt21" id="srGpAmtRow" aria-label="Change the amount"><span class="numw"><span class="cur" id="srGpCur">$</span><span class="num" id="srGpNum">0</span></span></button>
        <input id="srReqRef" class="gp-note" maxlength="40" autocomplete="off" enterkeyhint="done" placeholder="Add a note (optional)" aria-label="Add a note (optional)" />
        <button type="button" class="gp-share" id="srGpShare">Share</button>
        <p class="gp-status" id="srGpStatus" role="status" aria-live="polite"></p>
      </div>

      <div id="srOverlay" class="drop-overlay" hidden></div>
    </section>
  `;

  const $ = <T extends HTMLElement>(sel: string) => container.querySelector<T>(sel)!;

  // --- state ---------------------------------------------------------------
  let mode: Mode = "send"; // v1.1.21 — the home sends; the header's Get paid toggle asks

  // --- v0.90.19 — the ladder of sources: rung 3 ---------------------------
  // The pockets that could top up a shortfall in the pay currency, in the
  // order the design fixes (default first, then largest), each with what it
  // could cover: its cash at the rate, bounded by the per-swap cap (none in
  // production), and the buffer's balance in the pay currency. The rung is
  // simply absent when the allowance is spent or nothing can cover anything.
  type Rung = { source: string; rate: number; capOut: number };
  let rungs: Rung[] = [];
  async function refreshLadder() {
    const fx = deps.fx;
    const L = deps.ladder;
    if (!fx?.capacity || !L) { rungs = []; return; }
    const to = cur();
    const pockets = L.pockets();
    const def = L.defaultPocket();
    const cands = liveCurrencies()
      .filter((c) => c !== to && (pockets[c] ?? 0) > 0)
      .sort((a, b) => (a === def ? -1 : b === def ? 1 : 0) || (pockets[b] ?? 0) - (pockets[a] ?? 0));
    const out: Rung[] = [];
    for (const from of cands) {
      try {
        const c = await fx.capacity(from, to);
        if (c.remaining === 0) { rungs = []; return; } // allowance spent: no rung
        let capOut = (pockets[from] ?? 0) * c.rate;
        if (c.maxIn != null) capOut = Math.min(capOut, c.maxIn * c.rate);
        if (c.bufferOut != null) capOut = Math.min(capOut, c.bufferOut);
        capOut = Math.floor(capOut * 100) / 100;
        if (capOut > 0) out.push({ source: from, rate: c.rate, capOut });
      } catch {
        /* this pocket can't help right now — try the next */
      }
    }
    if (cur() === to) rungs = out; // the pocket may have changed while we asked
  }
  const ladderMax = () => rungs.reduce((m, r) => Math.max(m, r.capOut), 0);
  /** The keypad's ceiling: the pocket, plus what rung 3 could add. */
  const ceiling = () => Math.round(deps.getAvailable() * 100) / 100 + ladderMax() + 1e-9;
  type Plan = { rung: Rung; shortfall: number; sourceAmount: number };
  function planFor(amount: number): Plan | null {
    const pocket = Math.floor(deps.getAvailable() * 100 + 1e-7) / 100; // 1.4.60: floored — a pocket reading £0.40 may hold 0.399999 (payLadder.ts)
    const shortfall = Math.round((amount - pocket) * 100) / 100;
    if (shortfall <= 0) return null;
    const rung = rungs.find((r) => r.capOut + 1e-9 >= shortfall);
    if (!rung) return null;
    return { rung, shortfall, sourceAmount: Math.ceil((shortfall / rung.rate) * 100) / 100 };
  }
  let plan: Plan | null = null; // the plan the card shows; the slide executes it
  function paintSource() {
    const row = $("#pcSrcRow");
    if (!plan) { row.hidden = true; return; }
    const pocket = Math.round(deps.getAvailable() * 100) / 100;
    const converted = `${formatFx(plan.rung.source, plan.sourceAmount)} converted`;
    $("#pcSrc").textContent = pocket > 0 ? `${fmt(pocket)} + ${converted}` : converted;
    row.hidden = false;
  }
  /** The top-up for the current plan, or null when the pocket covers it.
   *  Throws FxRefused (a sentence) when the Worker won't price it. */
  async function topUpFor(): Promise<FxTopUp | null> {
    if (!plan || !deps.fx?.topUp) return null;
    const t = await deps.fx.topUp(plan.rung.source, cur(), plan.shortfall);
    // Decision 3 (22 Sep): if the voucher prices the source side materially
    // away from the card, re-quote and ask for another slide — never settle
    // a figure the person didn't see. Same tolerance as the Swap sheet.
    const diff = Math.abs(t.amountIn - plan.sourceAmount); // 1.4.60: more than 1 % AND more than two cents (a cent on a small shortfall is not a move)
    if (diff > 0.02 + 1e-9 && diff / plan.sourceAmount > 0.01) {
      plan = { ...plan, sourceAmount: t.amountIn };
      paintSource();
      throw new Error("rate_moved");
    }
    plan = { ...plan, sourceAmount: t.amountIn };
    return t;
  }
  // The contact chosen from My M® contacts (or handed in by a pay link); null = M® Note.
  let picked: { address: string; label: string; code?: string; raw?: boolean } | null = null; // raw = a pasted 0x address (v1.3.3)
  const statusEl = $<HTMLElement>("#srStatus");
  // 1.4.74 — the M® Note foot note's state (syncCashNote, below); declared here, before anything can call showStep.
  let cashNoteWanted = false; // the card is on M® Note
  let cashNoteGone = false; // slid on this card: not again until the next one
  let feeLine = ""; // the fee's words, once known

  // --- v1.1.21 — the amount: money pills + keypad, additive -----------------
  // The amount is the pills' sum plus the number typed on the keypad: $5 then
  // "1" is $6, a second tap on $50 is $100. Delete takes the whole amount
  // down digit by digit to 0; ✕ clears it. Past the balance the first amount
  // that passes it is taken and shown greyed, and nothing more is added until
  // it comes back (Alexander, 4 Oct 16:17).
  let pillSum = 0;
  let typed = "";
  let touched = false; // the keypad turns to glass at the first key
  let over = false;
  const round2 = (n: number) => Math.round(n * 100) / 100;
  const amtNumber = () => round2(pillSum + Number(typed || "0"));
  const hasAmt = () => pillSum > 0 || typed.length > 0;
  const sym = () => currency(cur()).symbol;
  /** The home's way of writing money: $50, $14.50, $1,250. */
  const money = (n: number) =>
    sym() + n.toLocaleString("en-US", Number.isInteger(n) ? { maximumFractionDigits: 0 } : { minimumFractionDigits: 2, maximumFractionDigits: 2 });
  /** The amount as the keypad holds it (no grouping): "12.", "5", "14.50". */
  const amtText = () => {
    if (typed && pillSum === 0) return typed;
    const n = amtNumber();
    return Number.isInteger(n) ? String(n) : n.toFixed(2);
  };
  const total = () => deps.getTotal?.() ?? deps.getAvailable();
  const isOver = (n: number) => mode === "send" && n > ceiling();
  function setAmt(ps: number, ty: string) {
    pillSum = ps;
    typed = ty;
    over = hasAmt() && isOver(amtNumber());
    deps.onOver?.(over);
    renderAmt();
  }

  /** The keypad's figure as written ("1,250.5": grouped, the typed decimals as typed) and its size step. 1.5.8 — Sending and
   *  Get paid's request write the same text at the same size (.amt21), so the amount doesn't change between the screens. */
  function shownAmt() {
    const [ints, decs] = amtText().split(".");
    const intDigits = (ints || "0").length;
    return {
      text: Number(ints || "0").toLocaleString("en-US") + (decs != null ? `.${decs}` : ""),
      size: intDigits <= 3 ? "60px" : intDigits === 4 ? "52px" : intDigits <= 6 ? "44px" : "36px",
    };
  }
  function renderAmt() {
    const el = $("#srAmt");
    const shown = shownAmt();
    el.textContent = shown.text;
    $("#srCur").textContent = sym();
    const wrap = $("#srAmtWrap");
    wrap.style.setProperty("--amt-size", shown.size);
    wrap.classList.toggle("over", over);
    const has = hasAmt();
    const live = has && !over && amtNumber() > 0;
    paintPill(has, live);
    $("#srPad").hidden = mode === "invest"; // 1.4.94 — Invest: no keypad (the pills: paintPockets)
    const glass = touched || has;
    $("#srAmount").classList.toggle("kp-rest", !glass); // v1.1.28 — Send rests dimmed with the keypad
    for (const id of ["#srPad", "#srPills"]) {
      $(id).classList.toggle("glass", glass);
      $(id).classList.toggle("rest", !glass);
    }
    container.querySelectorAll<HTMLElement>("#srPills .pcur").forEach((s) => (s.textContent = sym()));
    paintPockets();
    deps.onActivity?.(has);
    paintSay();
  }

  $("#srPad").addEventListener("click", (e) => {
    const btn = (e.target as HTMLElement).closest<HTMLButtonElement>("button");
    if (!btn) return;
    const k = btn.dataset.k!;
    touched = true;
    answered();
    if (k === "back") {
      const next = amtText().slice(0, -1);
      setAmt(0, next === "0" ? "" : next);
      return;
    }
    if (over) { nudgePill(); return; } // nothing more past the balance
    if (k === ".") {
      if (pillSum > 0) { setAmt(0, `${amtText()}${amtText().includes(".") ? "" : "."}`); return; } // the point joins the pills' sum
      if (!typed.includes(".")) setAmt(0, (typed || "0") + ".");
      return;
    }
    if (/\.\d\d$/.test(typed)) return; // two decimals at most
    const t = (typed === "0" ? "" : typed) + k;
    if (pillSum + Number(t) > MAX_AMOUNT) return; // cap fat-finger amounts
    setAmt(pillSum, t);
  });
  $("#srPills").addEventListener("click", (e) => {
    const btn = (e.target as HTMLElement).closest<HTMLButtonElement>("button");
    if (!btn) return;
    touched = true;
    answered();
    if (over) { nudgePill(); return; }
    const next = round2(pillSum + Number(btn.dataset.v));
    if (next + Number(typed || "0") > MAX_AMOUNT) return;
    setAmt(next, typed);
  });
  $("#srGpShow").addEventListener("click", () => showGp("qr")); // 1.4.94 — Get paid's QR, from the amount screen
  // 1.4.94 — the dock's split pill: never disabled (at rest it opens the mode menu); lit (.on) when the amount can go
  // on; the word Next with an amount (1.4.91), else the mode's verb; the segment ✕ with an amount, else ⌄.
  // 1.5.1 (Alexander, 24 Oct) — on Get paid's request it reads Done | ✕, lit: Done goes home (Send), ✕ back to the amount.
  function paintPill(has = hasAmt(), live = has && !over && amtNumber() > 0) {
    const { btn: go, x, lbl } = deps.dockGo;
    const done = mode === "receive" && gpStage === "link" && !$("#srGpLink").hidden;
    go.classList.toggle("on", live && !done); // 1.5.3 (Alexander) — Done on Get paid's request reads as Send at rest does, not green
    go.dataset.has = has || done ? "1" : "";
    const verb = mode === "send" ? "Send" : mode === "receive" ? "Get paid" : "Invest";
    lbl.textContent = done ? "Done" : has ? "Next" : verb;
    go.setAttribute("aria-label", done ? "Done: back to sending" : has ? `${verb} ${money(amtNumber())}: next` : `${verb} — tap for Send, Get paid or Invest`);
    x.innerHTML = has || done ? IC_CLOSE : IC_CHEVRON_DOWN;
    x.classList.toggle("in", has || done);
    x.setAttribute("aria-label", done ? "Back to the amount" : has ? "Clear" : "Send, Get paid or Invest");
  }
  const clearAmt = () => {
    if (mode === "receive" && gpStage === "link") { showGp("amount"); return; } // 1.5.1 — ✕ on the request: back to the amount
    touched = false; setAmt(0, "");
  };
  /** A refused key: the balance pill nudges once. */
  function nudgePill() {
    const p = document.querySelector<HTMLElement>("#nucPillBtn");
    if (!p || reduceMotion()) return;
    try { p.animate([{ transform: "translateX(0)" }, { transform: "translateX(-5px)" }, { transform: "translateX(4px)" }, { transform: "translateX(-2px)" }, { transform: "translateX(0)" }], { duration: 320, easing: "ease-out" }); } catch { /* no WAAPI */ }
  }
  /** 1.5.4 — the low-balance line centred between the keypad's last row and the dock (measured, as on Swap). */
  function placeLow() {
    const line = $("#srLine");
    if ($("#srAmount").hidden || line.hidden) return;
    const kb = $("#srPad").getBoundingClientRect().bottom;
    const dock = document.querySelector<HTMLElement>(".dock-inner")?.getBoundingClientRect();
    const dt = dock && dock.height ? dock.top : innerHeight - 90;
    line.style.top = `${Math.round((kb + dt) / 2 - line.offsetHeight / 2)}px`;
  }
  addEventListener("resize", () => { if (container.isConnected && $("#srLine").classList.contains("low21")) placeLow(); });
  /** 1.5.1 — the typed amount's top on the home, so the Sending step and Get paid's request put theirs there. */
  let amtTop = 0;
  const noteAmtTop = () => { const w = $("#srAmtWrap"); if (!w.hidden && !$("#srAmount").hidden) amtTop = $("#srAmt").getBoundingClientRect().top; }; // 1.5.8 — the figure's top, not its row's
  /** Moves `el` (by its margin-top) so its top stands at the home amount's. */
  const alignTo = (el: HTMLElement) => {
    if (!amtTop) return;
    el.style.marginTop = "";
    // 1.5.8 — the figure's top to the keypad figure's (the rows' boxes differ by a pixel; 1.5.1–1.5.6: the rows' tops)
    const d = amtTop - (el.querySelector<HTMLElement>(".num") ?? el).getBoundingClientRect().top;
    if (Math.abs(d) > 0.05) el.style.marginTop = `${(parseFloat(getComputedStyle(el).marginTop) || 0) + d}px`;
  };
  const home = () => { resetToAmount(); setMode("send"); };
  const goNext = () => {
    if (mode === "receive" && gpStage === "link") { home(); return; } // 1.5.1 — Done: the home, sending
    if (!(amtNumber() > 0) || over) return;
    noteAmtTop();
    if (mode === "receive") showGp("link"); // v1.1.23 — Get paid $X → the request
    else enterDetails();
  };

  // --- v1.1.25 — Global: the pockets row ---------------------------------------
  // Up to three of your pockets (the active one always among them), the last
  // with a chevron that opens the currency list, and the Swap squircle at the
  // end. A tap makes that pocket the one the home works in.
  let global = false;
  // v1.1.30 — only your pockets: the default first (no list), the newest
  // second, the most used third (USD when the first two aren't); pills 2 and 3
  // open the list of your pockets, and a pick there sets that pill (fx.ts).
  function pocketCodes(): string[] {
    return pocketRow();
  }
  function paintPockets() {
    const box = $("#srPockets");
    const on = global && mode === "send";
    box.hidden = !on;
    $("#srPills").hidden = on || mode === "invest"; // 1.4.94 — Invest shows no keypad and no pills
    if (!on) return;
    const codes = pocketCodes();
    const c = cur();
    box.style.gridTemplateColumns = `repeat(${codes.length}, minmax(0, 1fr)) 52px`;
    box.innerHTML = codes
      .map((code, i) => {
        // v1.1.31 — no chevron: pills 2 and 3 open the list on press-and-hold.
        // v1.1.29 — the symbol only ($ € £); the code is the button's name.
        return `<button type="button" class="k21 pk${code === c ? " on" : ""}" data-code="${code}" data-slot="${i + 1}"${i > 0 ? ' data-list="1"' : ""} aria-pressed="${code === c}" aria-label="${escapeHtml(currency(code).name)}"><b>${escapeHtml(currency(code).symbol.trim())}</b></button>`;
      })
      .join("") + `<button type="button" class="swapsq" id="srSwapSq" aria-label="Swap">${IC_SWAP_UD}</button>`;
  }
  $("#srPockets").addEventListener("click", (e) => {
    const t = e.target as HTMLElement;
    if (t.closest("#srSwapSq")) { deps.onSwap?.(); return; }
    const b = t.closest<HTMLButtonElement>(".pk");
    if (!b) return;
    if (held) { held = false; return; } // the hold already acted
    deps.onPocket?.(b.dataset.code!);
  });
  // v1.1.31 — press and hold pill 2 or 3: the list of your pockets; a pick
  // sets that pill until another is picked there (a tap just selects).
  let held = false;
  let holdT: number | undefined;
  $("#srPockets").addEventListener("pointerdown", (e) => {
    const b = (e.target as HTMLElement).closest<HTMLButtonElement>(".pk[data-list]");
    if (!b) return;
    held = false;
    holdT = window.setTimeout(() => {
      held = true;
      if (navigator.vibrate) navigator.vibrate(8);
      const code = b.dataset.code!;
      const slot = Number(b.dataset.slot) === 2 ? 2 : 3;
      deps.onPocket?.(code);
      paintPockets();
      deps.onPocketList?.(code, (picked) => {
        setPocketSlot(slot, picked);
        deps.onPocket?.(picked);
        paintPockets();
      });
    }, 550);
  });
  for (const ev of ["pointerup", "pointerleave", "pointercancel"] as const) $("#srPockets").addEventListener(ev, () => window.clearTimeout(holdT));

  // --- v1.1.21 — the conversation space -------------------------------------
  // At rest: "Hi Alex." and the question. A message line REPLACES them (one
  // layer, never both) in the question's serif, with its answer chips under
  // it; over the balance the line keeps its place and the chips go under the
  // amount. A line that waits for an answer quiets the pills and the keypad;
  // a key or a pill answers it as "not now". Scripted lines only: nothing
  // leaves the phone to write them (AI is parked).
  type Chip = { label: string; cls?: "quiet" | "alert"; tap: () => void };
  // v1.1.27 — the first thing after a login, once per login (this view is
  // built once per login). A key retires it.
  // v1.3.3 (Alexander, 6 Oct 16:25) — no pills under it (✕ · Send · Get paid
  // are gone): the line stands alone over the keypad at rest, and the keypad
  // is the way on. v1.3.1 — "doing well." in the line is the button (the
  // slider's green) and opens Activity.
  // v1.3.0 (Alexander, 6 Oct) — the line is "Your money's doing well." (it was
  // "Sending, or getting paid?"). It is only said to a wallet with money in
  // it: an empty one opens on "Add money to get started?", and a wallet just
  // made with an invite on "Welcome. Sending $10 to your wallet now to try M®."
  // — once, with the amount that arrived.
  let welcomeAsk = true;
  let welcomeSaid = false; // the welcome-money line was answered
  const notNow = new Set<string>(); // serials whose line was answered, for this visit
  let copied: string | null = null; // after Copy code / Copy link: what to do with it
  let untaken: SentNote | null = null;
  const UNTAKEN_AFTER_MS = 15 * 60_000;
  // 1.4.87 (Alexander, 22–23 Oct) — broadcast: messages from M (src/broadcast.ts). The live ones are read when the
  // home is built and again when the phone comes back to it (at most once a minute); a Worker that doesn't answer
  // means no message. Dismissed here = gone from this browser (bcGone covers a private window, where storage fails).
  let bcItems: Broadcast[] = [];
  const bcGone = new Set<number>();
  const bcRead = (force = false) => void liveBroadcasts(force).then((x) => { bcItems = x; if (container.isConnected) paintSay(); });
  bcRead(true);
  document.addEventListener("visibilitychange", () => { if (document.visibilityState === "visible" && container.isConnected) bcRead(); });
  /** One message in its frame: the glyph, the headline and the words — as text, never markup — and ✕ when it can go. */
  function paintBc(el: HTMLElement, b: Broadcast | null, closable: boolean) {
    el.hidden = !b;
    if (!b) { el.replaceChildren(); delete el.dataset.id; return; }
    if (el.dataset.id === String(b.id)) return;
    el.dataset.id = String(b.id);
    el.className = `bc21 k-${b.kind}${closable ? " closable" : ""}`;
    el.setAttribute("aria-label", "Notice from M");
    const g = document.createElement("span");
    g.className = "bc-g";
    g.innerHTML = BC_GLYPH[b.kind]; // the app's own glyph, not the message's
    const t = document.createElement("div");
    t.className = "bc-t";
    const h = document.createElement("p");
    h.className = "bc-h";
    h.textContent = b.title;
    const p = document.createElement("p");
    p.className = "bc-b";
    p.textContent = b.body;
    t.append(h, p);
    el.replaceChildren(g, t);
    if (closable) {
      const x = document.createElement("button");
      x.type = "button";
      x.className = "bc-x";
      x.setAttribute("aria-label", "Dismiss");
      x.innerHTML = `<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.2" stroke-linecap="round" aria-hidden="true"><path d="M6 6l12 12M18 6 6 18"/></svg>`;
      x.addEventListener("click", () => { dismissBroadcast(b.id); bcGone.add(b.id); paintSay(); });
      el.append(x);
    }
  }
  /** A key or a pill answers a waiting question with "not now". */
  function answered() {
    if (typeof deps.welcomeMoney?.() === "number" || deps.welcomeNote?.()) welcomeSaid = true;
    if (welcomeAsk) { welcomeAsk = false; return; } // the opening line was the one waiting
    if (untaken?.serial && !hasAmt()) notNow.add(untaken.serial);
    copied = null;
  }
  function paintSay() {
    // 1.4.87 — broadcast first: the critical banner whatever the home is doing; an info or warning panel in place of
    // the home's own lines while nothing is typed (sending, globe off) — dismissed, the lines come back.
    const bc = pickBroadcast(bcItems, (id) => bcGone.has(id) || isDismissed(id));
    paintBc($("#srBcBanner"), bc.banner, false);
    const notice = mode === "send" && !hasAmt() && !global ? bc.panel : null;
    $("#srSay").classList.toggle("inv21", mode === "invest" && !notice); // 1.5.3 — Invest's line where the home's main line stands
    paintBc($("#srBcPanel"), notice, true);
    if (notice) {
      for (const id of ["#srGreet", "#srAsk", "#srLine", "#srChips", "#srAmtWrap"]) $(id).hidden = true;
      $("#srSay").classList.remove("with-amt");
      $("#srPad").classList.remove("hush");
      $("#srPills").classList.remove("hush");
      return;
    }
    if (mode === "invest") {
      // 1.4.94 (Alexander, 24 Oct) — Invest is members only for now: the serif line and three dimmed tiles, no keypad.
      for (const id of ["#srLine", "#srChips", "#srAmtWrap"]) $(id).hidden = true;
      // 1.5.3 (Alexander, 24 Oct) — Members only stands where the home's main line stands: the greeting keeps its room,
      // unseen (CSS .inv21), and the tiles hang under the line, lower, outside the centred block.
      const greet = $("#srGreet"); greet.hidden = false; if (!greet.textContent?.trim()) greet.textContent = "\u00a0";
      const ask = $("#srAsk"); ask.hidden = false; ask.className = "s-ask";
      ask.innerHTML = `Members only<span class="mem21"><span>MMF</span><span>ETF</span><span>Gold</span></span>`;
      $("#srSay").classList.remove("with-amt");
      return;
    }
    let text = "";
    let chips: Chip[] = [];
    let asking = false;
    // v1.1.33 — a phrase inside the line that is itself the button (the slider's green).
    let lineAct: { label: string; tap: () => void } | null = null;
    let evenChips = false; // 1.5.60 — two chips of one width (the Timelock's Cancel | Send)
    const addMoney = () => deps.onAddMoney?.();
    // v1.3.0 — what the home opens on (sending, nothing typed).
    const wm = deps.welcomeMoney?.() ?? null;
    const wn = deps.welcomeNote?.() ?? null; // 1.4.57
    const funded = deps.funded ? deps.funded() : total() > 0;
    const tlSay = mode === "send" && !hasAmt() && !global ? timelockSay() : null; // 1.5.59 — a hold's line before anything else of the home's
    let opening: "welcome" | "well" | "quiet" | "empty" | null = null;
    if (mode === "send" && !hasAmt() && !tlSay) {
      if ((typeof wm === "number" || wn) && !welcomeSaid) opening = "welcome";
      else if (welcomeAsk) opening = wm === "wait" || funded === null ? "quiet" : funded ? "well" : "empty";
    }
    const opened = opening === "welcome" || opening === "well"; // the opening line shows
    if (hasAmt()) {
      if (over) {
        // v1.1.33 — "Low balance. Add money?": the phrase is the button; no
        // chips, the dock untouched. v1.3.0 — the empty wallet's line too.
        const bal = Math.floor(deps.getAvailable() * 100) / 100;
        text = bal > 0 ? "Low balance. Add money?" : "Add money to send that?";
        lineAct = { label: bal > 0 ? "Add money?" : "Add money", tap: addMoney };
      }
    } else if (mode === "send") {
      // 1.4.22 (the video, 16 Oct) — not in Global mode: back from a swap, the home asked about an
      // old note over a hushed keypad. The question waits for the sending home (globe off).
      const rec = untaken && !global && !notNow.has(untaken.serial ?? "") ? untaken : null;
      if (tlSay) {
        text = tlSay.text;
        chips = tlSay.chips;
        // 1.5.60 — never hushed: a hold lasts hours or days and the wallet keeps working under it (Alexander's
        // recordings of 4 Nov: the keypad and the pockets looked shut while a hold was open).
        asking = false;
        evenChips = true;
      } else if (opened) {
        // painted below, in the prompt's place; the keypad stays as it rests.
        // 1.5.45 (HL-17) — or, on the day's first home of a wallet worth protecting, the Spare Key prompt in its place:
        // the message line with its phrase as the button (as "Add money to get started?"), Hi <name>. above it.
        if (opening === "well" && deps.sparePrompt?.()) {
          text = SPARE_LINE;
          lineAct = { label: SPARE_ACT, tap: () => { welcomeAsk = false; paintSay(); deps.onSparePrompt?.(); } };
          deps.onSparePromptShown?.();
        }
      } else if (opening === "quiet") {
        // the greeting alone, until the balance is read (or the welcome money lands)
      } else if (copied) {
        text = copied;
        chips = [{ label: "OK", cls: "quiet", tap: () => { answered(); paintSay(); } }];
        asking = true;
      } else if (funded === false && wm === null) { // (not while welcome money is on its way)
        // v1.3.0 (Alexander, 6 Oct) — a wallet with nothing in it: "Add money"
        // in the line is the button, as "Add money?" is over the balance. No chips.
        text = "Add money to get started?";
        lineAct = { label: "Add money", tap: addMoney };
      } else if (rec?.secret && rec.serial) {
        const code = rec.kind === "code";
        text = `Your ${formatNoteAmount(sentCurrency(rec), rec.amount).replace(/\.00$/, "")} note hasn’t been taken. Copy the ${code ? "code" : "link"} again?`;
        chips = [
          {
            label: code ? "Copy code" : "Copy link",
            tap: () => {
              const t = code ? formatCode(rec.secret!) : noteLink(LINK_ORIGIN, rec.serial!, rec.secret);
              void navigator.clipboard.writeText(t).then(
                () => { copied = code ? "Code copied. Say it, don’t send it with the link." : "Link copied. Whoever has it can take the note."; paintSay(); },
                () => { copied = "Couldn’t copy. It’s in Recent transactions."; paintSay(); },
              );
            },
          },
          { label: "Not now", cls: "quiet", tap: () => { notNow.add(rec.serial!); paintSay(); } },
        ];
        asking = true;
      }
    }
    const name = deps.greetName?.() ?? "";
    $("#srGreet").textContent = name ? `Hi ${name}.` : "Hi.";
    // v1.1.29 — "Hi Alex." once per login: it greets with the opening line and goes with it.
    $("#srGreet").hidden = !opening;
    // v1.1.28 — no "How much to send?" (Alexander, 5 Oct): sending rests on the greeting alone.
    const ask = $("#srAsk");
    ask.hidden = !!text || hasAmt() || (mode === "send" && !opened);
    ask.textContent =
      opening === "welcome" ? (wn ? wn : `Welcome. Sending ${formatUsdShort(wm as number).replace(/\.00$/, "")} to your wallet now to try M®.`)
      : opening === "well" ? "Your money’s "
      : mode === "send" ? "" : "How much to ask for?";
    if (opening === "well") {
      // v1.3.1 — "doing well." is the button: it opens Activity (and retires the line, as the pill did).
      const b = document.createElement("button");
      b.type = "button";
      b.className = "line21-act";
      b.textContent = "doing well.";
      b.addEventListener("click", () => { welcomeAsk = false; paintSay(); deps.onCheckMoney?.(); });
      ask.append(b);
    }
    ask.classList.toggle("w21", opening === "well");
    ask.classList.toggle("w21-long", opening === "welcome");
    const line = $("#srLine");
    line.textContent = "";
    const at = lineAct ? text.indexOf(lineAct.label) : -1;
    if (lineAct && at >= 0) {
      const b = document.createElement("button");
      b.type = "button";
      b.className = "line21-act";
      b.textContent = lineAct.label;
      b.addEventListener("click", lineAct.tap);
      line.append(text.slice(0, at), b, text.slice(at + lineAct.label.length));
    } else {
      line.textContent = text;
    }
    line.hidden = !text;
    // 1.5.4 (Alexander, 24 Oct) — past the balance, the line is Swap's: under the keypad, midway to the dock, the words in
    // the alert colour and the button green (1.1.33–1.5.3: above the amount, in the line's colour). Send and Global alike.
    const low = hasAmt() && over && !!text;
    line.classList.toggle("low21", low);
    // 1.5.46 — the Spare Key prompt is fixed text that always fits two lines: no clamp (WebKit put an ellipsis after
    // the phrase-button at the clamp's edge, "Make a Spare Key.…", Alexander's iPhone, 30 Oct); every other line keeps it.
    line.classList.toggle("spare21", text === SPARE_LINE);
    if (low) placeLow(); else line.style.top = "";
    // 1.4.15 (Alexander, 16 Oct) — Global on: the amount line shows ($0 before a key), unless a message
    // is up in its place (an untaken note, "Add money to get started?", a copy's confirmation…).
    const showAmt = hasAmt() || (global && mode === "send" && !text && !opened && opening !== "quiet");
    $("#srAmtWrap").hidden = !showAmt;
    $("#srSay").classList.toggle("with-amt", showAmt);
    const box = $("#srChips");
    box.innerHTML = chips.map((c, i) => `<button type="button" class="chip21${c.cls ? ` ${c.cls}` : ""}" data-i="${i}">${escapeHtml(c.label)}</button>`).join("");
    box.hidden = !chips.length;
    box.classList.toggle("even", evenChips && chips.length > 1);
    box.querySelectorAll<HTMLButtonElement>("button").forEach((b) => b.addEventListener("click", () => chips[Number(b.dataset.i)].tap()));
    $("#srPad").classList.toggle("hush", asking);
    $("#srPills").classList.toggle("hush", asking);
  }
  /** Read this browser's sent notes against Arc (at most every 20 s), then find one nobody has taken. */
  let untakenReadAt = 0;
  async function refreshUntaken() {
    if (listSent().some((r) => !r.takenAt) && Date.now() - untakenReadAt > 20_000) {
      untakenReadAt = Date.now();
      try { await reconcileSent(notes); } catch { /* a failed read changes nothing */ }
    }
    untaken = listSent().find((r) => !r.takenAt && !!r.secret && !!r.serial && Date.now() - r.at > UNTAKEN_AFTER_MS) ?? null;
    if (!$("#srAmount").hidden) paintSay();
  }

  /** v1.1.21 — sending or getting paid (the header's toggle); the amount travels. */
  function setMode(next: Mode) {
    if (next !== mode) {
      mode = next;
      over = hasAmt() && isOver(amtNumber());
      deps.onOver?.(over);
    }
    deps.onMode?.(mode);
    if (mode === "receive") { showGp("amount"); return; } // 1.4.94 — Get paid opens on the amount (v1.1.23–1.4.93: your QR; Show my QR leads there)
    hideGp();
    if ($("#srAmount").hidden) showStep("amount");
    else renderAmt();
  }

  // --- v1.1.23 — Get paid: your QR · the amount · the request ----------------
  // The QR and its outline start at the same top on all three screens (the
  // amount screen's is smaller and dimmed, green-outlined). After Share the
  // home stays in Get paid mode, on your QR; the header's toggle or the dock's
  // centre goes back to sending.
  type GpStage = "qr" | "amount" | "link";
  let gpStage: GpStage = "qr";
  const qrBox = (url: string) => `<div class="gp-code">${brandedQrSvg(url, 6, 2)}</div>`;
  const walletUrl = () => name?.url ?? "";
  function hideGp() {
    $("#srAmount").classList.remove("gpmode");
    $("#srGp").hidden = true;
    $("#srGpLink").hidden = true;
  }
  function showGp(stage: GpStage) {
    gpStage = stage;
    const a = document.activeElement as HTMLElement | null;
    if (a && container.contains(a)) a.blur();
    lockViewport(false);
    $("#srDetails").hidden = true;
    $("#srAmount").hidden = stage !== "amount";
    $("#srGp").hidden = stage !== "qr";
    $("#srGpLink").hidden = stage !== "link";
    $("#srAmount").classList.toggle("gpmode", stage === "amount");
    deps.onCard?.(false);
    if (stage === "qr") {
      const url = walletUrl();
      $("#srGpQr").innerHTML = url ? qrBox(url) : `<p class="gp-wait">Your wallet code is still setting up. Try again in a moment.</p>`;
      deps.onActivity?.(false);
      paintPill();
      return;
    }
    if (stage === "amount") {
      $("#srGpSaid").textContent = "";
      renderAmt();
      return;
    }
    deps.onActivity?.(false);
    paintGpLink();
    alignTo($("#srGpAmtRow")); // 1.5.1 — the amount above the QR, where the home's stood
    paintPill();
  }
  function paintGpLink() {
    const b = buildRequestUrl();
    if (!b) {
      $("#srGpReqQr").innerHTML = `<p class="gp-wait">Your wallet code is still setting up. Try again in a moment.</p>`;
      $("#srGpLinkText").textContent = "";
      return;
    }
    $("#srGpReqQr").innerHTML = qrBox(b.url);
    $("#srGpLinkText").textContent = b.url.replace(/^https?:\/\//, "");
    $("#srGpCur").textContent = sym();
    const shown = shownAmt(); // 1.5.8 — the keypad's text at the keypad's size
    $("#srGpNum").textContent = shown.text;
    $("#srGpAmtRow").style.setProperty("--amt-size", shown.size);
    $("#srGpStatus").textContent = "";
  }
  const gpSay = (el: string, t: string) => {
    $(el).textContent = t;
    setTimeout(() => { if ($(el).textContent === t) $(el).textContent = ""; }, 2400);
  };
  $("#srGpAmt").addEventListener("click", () => showGp("amount"));
  $("#srGpAmtRow").addEventListener("click", () => showGp("amount"));
  $("#srGpCopy").addEventListener("click", async () => {
    const b = buildRequestUrl();
    if (!b) return;
    try { await navigator.clipboard.writeText(b.url); gpSay("#srGpStatus", "Link copied"); } catch { gpSay("#srGpStatus", "Couldn't copy. Press and hold the link."); }
  });
  $("#srGpShare").addEventListener("click", async () => {
    const b = buildRequestUrl();
    if (!b) return;
    // v1.3.1 — in the pocket's own currency, as the screen and the link are (it always wrote dollars).
    const ask = cur() === "USD" ? formatUsd(amtNumber()) : formatFx(cur(), amtNumber());
    // v1.4.1 (Alexander, 14 Oct) — "Please pay me [amount] on M:" and the link on a line of its own.
    // The link travels inside the text (the Drop's pattern, share.ts): a separate `url` is placed by
    // each app as it likes — after the words on the same line, or above them.
    const friendly = `Please pay me ${ask} on M${b.refDisp ? ` — ${b.refDisp}` : ""}:\n${b.url}`;
    const r = await shareOrCopy({ text: friendly, title: "M payment request", copy: friendly });
    if (r === "failed") { gpSay("#srGpStatus", "Couldn't share. Press and hold the link to copy it."); return; }
    // Shared (or copied): back to the home, still in Get paid mode, on your QR.
    pillSum = 0;
    typed = "";
    touched = false;
    $<HTMLInputElement>("#srReqRef").value = "";
    showGp("qr");
    gpSay("#srGpSaid", r === "copied" ? "Link copied. Paste it into a chat." : "Request shared.");
  });

  // --- step navigation -----------------------------------------------------
  function showStep(step: "amount" | "details") {
    if (mode === "receive" && step === "amount") { showGp("amount"); return; } // v1.1.23
    hideGp();
    $("#srAmount").hidden = step !== "amount";
    $("#srDetails").hidden = step !== "details";
    statusEl.textContent = "";
    if (step === "amount") {
      closeList();
      leaveCash();
      cashNoteGone = false; // 1.4.74 — the next card may show the foot again
      showCashNote(false);
      deps.onCard?.(false);
      // v1.0.6 — the keypad never sits under a lift or a keyboard lock left
      // behind by a field that lost focus without a blur (Chrome, backgrounded).
      deps.onLift?.(false);
      document.body.classList.remove("kb-lock");
      renderAmt();
    }
  }

  function lockViewport(on: boolean) {
    document.body.classList.toggle("kb-lock", on);
  }

  function enterDetails() {
    deps.onActivity?.(true);
    if (mode === "receive") { showGp("link"); return; } // v1.1.23
    showStep("details");
    // v0.70.18 — learn the pool's own names so the list never shows a bare code.
    void warmProfiles(contactsPool().map((c) => c.address)).then((changed) => {
      if (changed && !$("#srPeople").hidden) renderPeople();
    });
    paintCard();
    alignTo($("#pcAmtRow")); // 1.5.1 — the amount alone, where the home's stood
  }

  // --- v1.1.21 — the Sending card --------------------------------------------
  // One card for M® Note and people: the amount, then the scan squircle and
  // My M® contacts. Nobody chosen = M® Note (Drop an M® Note), the lock row under
  // the card; a contact picked = their name in a pill with ✕ (Slide to
  // confirm). No To field and no typed names: a new person is reached by
  // their link or QR — or, since v1.3.3, by a pasted wallet address or M
  // wallet code on the top line of My M® contacts. M® Note replaces the
  // gated "split" Drops.
  const card = $<HTMLElement>("#srCard");
  function setSlide(label: string) {
    // 1.5.1 (Alexander, 24 Oct) — the slider sits in the dock: its words are "Slide to send" whoever it pays (the
    // full sentence, e.g. "Send money to anyone", stays as its accessible name). 1.1.21–1.5.0: the sentence shown.
    $("#srSlideLbl").textContent = "Send"; // 1.5.33 — a tap button: "Send", centred
    $("#srSlide").setAttribute("aria-label", label);
  }
  function paintCard() {
    preloadSent(); // 1.5.9 — M's sound, fetched once (cached for good) while the card is read
    const amount = amtNumber();
    // 1.5.8 (Alexander, 24 Oct) — the amount as the keypad showed it: the muted symbol hung left, the figure in the amount's
    // colour at the keypad's size, where it stood (1.5.1–1.5.6: "$20" in one piece, the symbol at the figure's size)
    const shown = shownAmt();
    const amtEl = $("#pcAmt");
    amtEl.innerHTML = `<span class="numw"><span class="cur"></span><span class="num"></span></span>`;
    amtEl.querySelector<HTMLElement>(".cur")!.textContent = sym();
    amtEl.querySelector<HTMLElement>(".num")!.textContent = shown.text;
    amtEl.style.setProperty("--amt-size", shown.size);
    $("#pcAmtRow").setAttribute("aria-label", `${money(amount)} — change the amount`);
    statusEl.textContent = "";
    closeList();
    slider.reset();
    deps.onCard?.(true);
    const who = picked;
    $("#srWho").hidden = !!who;
    $("#srPicked").hidden = !who;
    $("#pcRefField").hidden = !who; // v1.1.24 — a payment to a person carries an optional note
    if (who) {
      $("#pcTo").innerHTML = whoHtml(who.address, who.label, who.code);
      leaveCash();
      showCashNote(false);
      $("#srSlide").hidden = false;
      setSlide("Send");
      plan = planFor(amount); // v0.90.19 — rung 3, disclosed on the card
      paintSource();
      paintTimelock(); // 1.5.59 — said before Face ID: this one waits
      if (!tlHere) prepareForCard(); // v0.90.29
      // v1.3.3 — a pasted 0x address M knows nothing about: an address looks
      // the same on every network, so say which one this payment goes on.
      if (who.raw && !cachedProfile(who.address) && !tlHere) statusEl.textContent = "Sending to a wallet address. Make sure it’s on Arc.";
      return;
    }
    tlHere = null;
    enterCash();
  }
  $("#srMine").addEventListener("click", () => openList());
  {
    const f = $<HTMLInputElement>("#srRef");
    f.addEventListener("pointerdown", () => lockViewport(true));
    f.addEventListener("focus", () => lockViewport(true));
    f.addEventListener("blur", () => lockViewport(false));
    f.addEventListener("keydown", (e) => { if (e.key === "Enter" && !e.isComposing) { e.preventDefault(); f.blur(); } });
  }
  $("#srScan").addEventListener("click", () => deps.onScan?.());
  $("#srUnpick").addEventListener("click", () => { picked = null; paintCard(); });

  // --- My M® contacts ---------------------------------------------------------
  // Your own contacts only (names you gave, then people you paid), most recent
  // first; a search field once there are more than eight. A tap picks them.
  const SEARCH_FROM = 9;
  function openList() {
    $<HTMLInputElement>("#srFind").value = "";
    resetPaste();
    renderPeople();
    $("#srPeopleVeil").hidden = false;
    $("#srPeople").hidden = false;
    try {
      if (!reduceMotion()) $("#srPeople").animate([{ opacity: 0, transform: "translateY(10px)" }, { opacity: 1, transform: "none" }], { duration: 220, easing: "cubic-bezier(0.2, 0, 0, 1)" });
    } catch { /* no WAAPI */ }
  }
  function closeList() {
    const f = $<HTMLInputElement>("#srFind");
    if (document.activeElement === f) f.blur();
    if (document.activeElement === pasteEl) pasteEl.blur();
    lockViewport(false);
    $("#srPeopleVeil").hidden = true;
    $("#srPeople").hidden = true;
    fitList();
  }
  /** 1.5.60 (Alexander's Chrome recording of 4 Nov) — while a field in the list has the keyboard, the list fits the space
   *  above it. Chrome on iOS now shrinks the page to that space (the viewport's interactive-widget=resizes-content, which
   *  only Chrome on Android had honoured) and adds its autofill bar, so the list — pinned 150 px from the top and 110 px
   *  from the bottom, for the dock — was squeezed to a strip: the field cut through, the page's line showing in it, no
   *  contacts. Safari keeps the page and lays the keyboard over it; the same sum moves the list up a little there. */
  function fitList() {
    const el = $("#srPeople");
    el.style.top = el.style.bottom = el.style.height = "";
    const vv = window.visualViewport;
    const a = document.activeElement;
    if (el.hidden || !vv || !(a instanceof HTMLInputElement) || !el.contains(a)) return;
    const r = el.getBoundingClientRect();
    const bottom = vv.offsetTop + vv.height - 12;
    const top = Math.max(vv.offsetTop + 12, Math.min(r.top, bottom - 260));
    el.style.top = `${Math.round(top)}px`;
    el.style.bottom = "auto";
    el.style.height = `${Math.round(Math.max(120, bottom - top))}px`;
  }
  window.visualViewport?.addEventListener("resize", () => { if (!$("#srPeople").hidden) fitList(); });
  $("#srPeopleX").addEventListener("click", closeList);
  $("#srPeopleVeil").addEventListener("click", closeList);
  {
    const f = $<HTMLInputElement>("#srFind");
    f.addEventListener("input", () => renderPeople());
    f.addEventListener("pointerdown", () => lockViewport(true));
    f.addEventListener("focus", () => { lockViewport(true); fitList(); });
    f.addEventListener("blur", () => setTimeout(fitList, 0));
    f.addEventListener("blur", () => lockViewport(false));
  }
  // v1.3.3 (Alexander, 6 Oct 16:25) — the top line of the list: paste (or
  // type) a 0x address or an M wallet code. The field's ✕ clears, its tick —
  // or Return — uses it: the wallet becomes the card's chosen person, as a
  // contact does. A code is looked up on the Worker (the pay page's own
  // /name/resolve); an address is taken as it is, checksum checked.
  const pasteEl = $<HTMLInputElement>("#srPaste");
  const pasteWrap = $("#srPasteWrap");
  let pasteBusy = false;
  function pasteMsg(text: string, err = false) {
    const m = $("#srPasteMsg");
    m.textContent = text;
    m.hidden = !text;
    m.classList.toggle("err", err);
  }
  function paintPaste() {
    const has = pasteEl.value.trim().length > 0;
    if (has) { pasteWrap.classList.remove("xout"); pasteWrap.classList.add("x", "on"); }
    else if (pasteWrap.classList.contains("x")) { pasteWrap.classList.remove("x", "on"); pasteWrap.classList.add("xout"); }
  }
  function resetPaste() {
    pasteEl.value = "";
    pasteBusy = false;
    pasteEl.disabled = false;
    pasteWrap.classList.remove("x", "on", "xout");
    pasteMsg("");
  }
  /** What was pasted: a 0x address, an M wallet code (also as the start of an mdrop.it link), or neither. */
  function readWallet(raw: string): { address: string } | { slug: string } | { error: string } {
    const t = raw.trim();
    if (/^0x[0-9a-fA-F]{40}$/.test(t)) {
      // viem checks the checksum of a mixed-case address: one wrong character is caught here.
      return isAddress(t) ? { address: getAddress(t) } : { error: "That address has a mistake in it. Copy it again." };
    }
    const link = t.match(/^(?:https?:\/\/)?(?:www\.)?mdrop\.it\/([^/?#\s]+)/i);
    const cand = (link ? link[1] : t).replace(/[\s-]/g, "");
    if (cand.length === WALLET_CODE_LEN && canonicalizeCode(cand).length === WALLET_CODE_LEN) return { slug: canonicalizeCode(cand) };
    return { error: "That isn’t a wallet address or an M® wallet code." };
  }
  /** The pasted wallet is on the card; if it is an M wallet, its own name and emoji follow once read. */
  function pickPasted(addr: string) {
    paintCard();
    void warmProfiles([addr]).then((changed) => {
      if (changed && picked?.address === addr && !$("#srDetails").hidden && $("#srPeople").hidden) {
        picked.label = contactLabel(addr, picked.code ?? null);
        $("#pcTo").innerHTML = whoHtml(addr, picked.label, picked.code);
        if (picked.raw && cachedProfile(addr)) statusEl.textContent = "";
      }
    });
  }
  async function usePaste() {
    if (pasteBusy || !pasteEl.value.trim()) return;
    const got = readWallet(pasteEl.value);
    if ("error" in got) { pasteMsg(got.error, true); return; }
    const self = address.toLowerCase();
    if ("address" in got) {
      if (got.address.toLowerCase() === self) { pasteMsg("That’s your own wallet.", true); return; }
      const known = contactsPool().find((c) => c.address.toLowerCase() === got.address.toLowerCase());
      picked = known
        ? { address: known.address, label: displayOf(known), code: known.code }
        : { address: got.address, label: contactLabel(got.address, null), raw: true };
      pickPasted(got.address);
      return;
    }
    pasteBusy = true;
    pasteEl.disabled = true;
    pasteMsg("Looking it up…");
    try {
      const r = await resolveName(got.slug);
      if ($("#srPeople").hidden) return; // the list was closed meanwhile
      if (r.address.toLowerCase() === self) { pasteMsg("That’s your own wallet.", true); return; }
      picked = { address: r.address, label: contactLabel(r.address, r.display), code: r.display };
      pickPasted(r.address);
    } catch (e) {
      const notFound = /→ 404\b/.test(e instanceof Error ? e.message : "");
      pasteMsg(notFound ? "No M® wallet has that code." : "Couldn’t look that up. Try again.", true);
    } finally {
      pasteBusy = false;
      pasteEl.disabled = false;
    }
  }
  pasteEl.addEventListener("input", () => { pasteMsg(""); paintPaste(); });
  pasteEl.addEventListener("pointerdown", () => lockViewport(true));
  pasteEl.addEventListener("focus", () => { lockViewport(true); fitList(); });
  pasteEl.addEventListener("blur", () => { lockViewport(false); setTimeout(fitList, 0); });
  pasteEl.addEventListener("keydown", (e) => { if (e.key === "Enter" && !e.isComposing) { e.preventDefault(); void usePaste(); } });
  $("#srPasteClr").addEventListener("click", () => { pasteEl.value = ""; pasteMsg(""); paintPaste(); pasteEl.focus(); });
  $("#srPasteDone").addEventListener("click", () => void usePaste());
  function renderPeople() {
    const self = address.toLowerCase();
    const pool = contactsPool().filter((c) => c.address.toLowerCase() !== self);
    $("#srFindWrap").hidden = pool.length < SEARCH_FROM;
    const q = $<HTMLInputElement>("#srFind").value.trim().toLowerCase();
    const rows = (q
      ? pool
          .filter((c) => nameIncludes(c, q) || (cachedProfile(c.address)?.name ?? "").toLowerCase().includes(q))
          .map((c, i) => ({ c, rank: (c.names.some((n) => n.toLowerCase().startsWith(q)) ? 0 : 1000) + i }))
          .sort((a, b) => a.rank - b.rank)
          .map((x) => x.c)
      : pool);
    const count = new Map<string, number>();
    for (const c of rows) count.set(displayOf(c).toLowerCase(), (count.get(displayOf(c).toLowerCase()) ?? 0) + 1);
    const box = $("#srPeopleRows");
    box.innerHTML = rows.length
      ? rows
          .map((c) => {
            const label = displayOf(c);
            const dup = (count.get(label.toLowerCase()) ?? 0) > 1 && c.code;
            return `<button class="li" type="button" data-addr="${escapeHtml(c.address)}" data-code="${escapeHtml(c.code ?? "")}" data-label="${escapeHtml(label)}"><span class="nm">${whoHtml(c.address, c.name, c.code)}</span>${dup ? `<small>${escapeHtml(c.code!)}</small>` : ""}</button>`;
          })
          .join("")
      : `<p class="empty">${q ? "Nobody by that name in your contacts." : "People you paid, and people who paid you, appear here.<br>You can simply send an M® Note — to anyone in the world."}</p>`;
    box.querySelectorAll<HTMLButtonElement>(".li").forEach((row) =>
      row.addEventListener("click", () => {
        const addr = row.dataset.addr!;
        const code = row.dataset.code ?? "";
        picked = { address: addr, label: row.dataset.label || contactLabel(addr, code), code: code || undefined };
        paintCard();
      }),
    );
  }

  // --- M® Note on the card -----------------------------------------------------
  let lockOn = true;
  /** Why this amount can't go as M® Note here, or "" when it can. */
  function cashBlocked(amount: number): string {
    // 1.4.54 — a note is in the pocket's currency when a series holds that token (src/noteCurrency.ts).
    const blocked = noteCurrencyBlocked(cur());
    if (blocked) return blocked;
    const have = deps.ladder?.pockets()?.[cur()] ?? deps.getAvailable();
    if (amount > round2(have) + 1e-9) return `An M® Note comes from your ${moneyWord(cur())}. Pick a contact, or change the amount.`;
    // v1.1.21 — any amount (Alexander, 4 Oct 16:50). Series A's note module
    // takes whole dollars only; the cash side's update takes cents. The card
    // asks the module itself, so nothing here changes when that lands.
    if (notes.live && !Number.isInteger(amount)) {
      try { notes.issueCalls(amount, "0x0000000000000000000000000000000000000001", ZERO_SALT, cur()); } catch {
        return "Notes with cents aren’t switched on yet. Make it whole dollars, or pick a contact.";
      }
    }
    return "";
  }
  function enterCash() {
    const amount = amtNumber();
    card.classList.add("cash");
    plan = null;
    paintSource();
    setSlide("Send money to anyone"); // v1.3.5 (Alexander, 7 Oct) — the M® Note slider's label
    lockOn = true; // per hand-over, never remembered
    paintLock();
    const why = cashBlocked(amount);
    noteRows(!why); // 1.4.56 (Alexander's screenshot, 20 Oct): no lock row for an amount that can't be cash
    if (why) {
      forgetCash();
      $("#srSlide").hidden = true;
      statusEl.textContent = why;
      showCashNote(false);
      return;
    }
    $("#srSlide").hidden = false;
    showCashNote(true);
    prepareCash(amount); // v1.1.15 — derive and prepare while the card is read
  }
  /** The card leaves M® Note (a contact picked, or back to the keypad). */
  function leaveCash() {
    if (!card.classList.contains("cash")) return;
    card.classList.remove("cash");
    noteRows(false);
    statusEl.textContent = "";
  }
  /** 1.5.3 — the note path's rows: the line, Lock and "or" show and go together. */
  function noteRows(on: boolean) {
    for (const id of ["#srNoteSay", "#srLock", "#srNoteOr"]) $(id).hidden = !on;
  }
  function paintLock() {
    const always = amtNumber() >= CASH_ALWAYS_LOCKED;
    const row = $("#srLock");
    row.classList.toggle("always", always);
    row.classList.toggle("off", !always && !lockOn);
    $("#srLockBox").hidden = always;
    $("#srLockIc").hidden = !always;
    $("#srLockBox").setAttribute("aria-checked", String(always || lockOn));
    $("#srLockT").textContent = always ? "Locked with a code" : "Lock it with a code";
    // Alexander, 4 Oct: the second sentence on a line of its own.
    // 1.4.74 (Alexander, 21 Oct): "Like cash: anyone with the link can take it" — no period; the foot no longer says it.
    $("#srLockS").innerHTML = always ? "Notes of $50 and up are always locked.<br>Share the link, say the code." : lockOn ? "" : "Like cash: anyone with the link can take it";
    $("#srLockS").hidden = !always && lockOn;
  }
  // The whole row is the checkbox's tap area (the box alone is small).
  $("#srLock").addEventListener("click", () => {
    if ($("#srLock").classList.contains("always")) return;
    lockOn = !lockOn;
    paintLock();
    if (card.classList.contains("cash")) ensurePrep(kindNow()); // v2.0 — the variant now shown, prepared if it wasn't
  });
  // The first three M® Note cards carry a note at the foot of the screen, until
  // three notes have gone or "Don't show again".
  // 1.4.74 (Alexander, 21 Oct 22:48): the note is the fee alone — "Network fee — €0.02" — with "Don't show again".
  // It shows once the fee is known (there is nothing to say before, or when there is no fee), and it goes at the
  // slide for the rest of the card, so a line under the slider (a cancelled Face ID) has the foot to itself; the next
  // card shows it again while the three last.
  const CASH_NOTE_KEY = "m.cashNote";
  const cashNoteLeft = () => {
    try { const v = localStorage.getItem(CASH_NOTE_KEY); return v === null ? 3 : Math.max(0, Number(v) || 0); } catch { return 0; }
  };
  function syncCashNote() {
    $("#srCashNote").hidden = !(cashNoteWanted && !cashNoteGone && feeLine !== "" && cashNoteLeft() > 0);
  }
  function showCashNote(on: boolean) {
    cashNoteWanted = on;
    syncCashNote();
  }
  function spendCashNote() {
    try { localStorage.setItem(CASH_NOTE_KEY, String(Math.max(0, cashNoteLeft() - 1))); } catch { /* storage blocked */ }
  }
  $("#srCashNoteNever").addEventListener("click", () => {
    try { localStorage.setItem(CASH_NOTE_KEY, "0"); } catch { /* storage blocked */ }
    showCashNote(false);
  });
  function cashNotLiveYet() {
    slider.reset();
    statusEl.textContent = "M® Notes aren't switched on yet. Nothing was sent.";
  }

  // --- v1.1.15 — M® Note, live (brief §8, §10) -----------------------------
  // While the card is read: the secret, the salt and the controller are
  // derived in a Web Worker (~0.5 s), then approve + issueNote is prepared as
  // one userOp — so the slide is one Face ID. Below $50 BOTH secrets are made,
  // the code first and then the link secret, so unticking never waits.
  // v2.0 (22 Oct, the Gas Station's cap) — only the variant the lock row shows
  // is PREPARED (the other when the row flips), with Circle's stub only; the
  // sponsor's signature is asked for when the finger lands on the thumb
  // (touchCash). No 45-s refresh: a stub-only preparation keeps for minutes
  // (PREPARED_TTL_MS) and its nonce is checked at the touch. A euro or pound
  // note's fee quote still lives 60 s: past 45 s the slide plans it again and
  // takes the plain path (one sponsorship, nothing orphaned).
  type CashSecret = { s: NewSecret; d: Derived };
  type CashPrep = CashSecret & { calls: Call[]; prepared: PreparedOp | null; fee: FeePlan }; // 1.4.71: the fee it was built with
  let cashDerived: Partial<Record<SecretKind, Promise<CashSecret | null>>> = {};
  let cashPreps: Partial<Record<SecretKind, Promise<CashPrep | null>>> = {};
  /** A used (or possibly used) secret is never offered again; nor is its preparation. */
  function forgetCash() {
    cashDerived = {};
    cashPreps = {};
  }
  let cashAmount = 0;
  let cashEnabled: Promise<boolean> = Promise.resolve(false);
  const notes = noteSource();
  const kindNow = (): SecretKind => (Math.floor(amtNumber()) >= CASH_ALWAYS_LOCKED || lockOn ? "code" : "link");
  // --- 1.4.71 (Alexander, 21 Oct) — the M® Note fee ---------------------------
  // A note carries its own gas (1.4.68): the key the code stands for cashes it in by itself, so the taker signs
  // nothing. The sender pays the fee — 2¢, from the Worker per wallet (/note/config) — in USDC to the key's address,
  // inside the issue's own userOp: from the dollars when the dollar pocket can spare it (1.4.84 — any currency);
  // otherwise, for a note in another currency, PRO RATA in that currency, converted through MFx in the same userOp
  // (the ladder's rung, as on the pay page). M may sponsor a wallet's notes: no fee, and the Worker pays the key's gas once Arc shows the issue.
  // A fee nobody's pockets can cover leaves the note without gas: it still goes, and takes the two-Face-ID way.
  // Said on the first three notes' foot line: "Network fee — €0.02" / "Network fee — on us" (1.4.74; was "Fee €0.02 —
  // Arc's network fee for the note." / "No fee — on us."). Shown in the note's currency, rounded UP to the cent: a euro
  // note pays the euros that buy exactly the fee in dollars (about €0.0172 for 2¢), so the line never says less than it costs.
  type FeePlan = { calls: NoteFeeCalls; shown: string; at: number; voucher: boolean } | { sponsored: true } | null;
  let feeInfo: Promise<NoteFee> | null = null; // one read per card session; the Worker's answer for this wallet
  const floor2c = (n: number) => Math.floor(n * 100 + 1e-7) / 100;
  const ceil2c = (n: number) => Math.ceil(n * 100 - 1e-7) / 100;
  async function planFee(amount: number): Promise<FeePlan> {
    const f = await (feeInfo ??= noteFee(address));
    if (f.sponsored) return { sponsored: true };
    const usd = Number(f.usd);
    if (!(usd > 0)) return null;
    const pockets = deps.ladder?.pockets() ?? { [cur()]: deps.getAvailable() };
    const spare = (c: string) => floor2c(pockets[c] ?? 0) - (c === cur() ? amount : 0);
    const direct = (): FeePlan => ({ calls: { units: feeUnits(usd) }, shown: formatNoteAmount("USD", usd), at: Date.now(), voucher: false });
    // 1.4.84 (Alexander, 22 Oct) — the dollars FIRST, for a note in any currency: the swap in a euro or pound note's
    // issue exists only to turn the fee into gas money (Arc's gas is USDC), and nearly every wallet holds dollars (the
    // beta starts there), so it bought a conversion the sender didn't need — ~2¢ of gas a note. The line then says
    // "Network fee — $0.05" on a euro note. The swap is the way when the dollar pocket can't spare the fee.
    if (spare("USD") + 1e-9 >= usd) return direct();
    if (cur() === "USD") return null;
    if (deps.fx?.topUp && spare(cur()) > 0) {
      try {
        const t = await deps.fx.topUp(cur(), "USD", usd); // exactly the fee in dollars out; the note's currency in, rounded up
        const cost = ceil2c(t.amountIn);
        if (cost <= spare(cur()) + 1e-9) return { calls: { units: feeUnits(usd), convert: t.calls }, shown: formatNoteAmount(cur(), cost), at: Date.now(), voucher: true };
      } catch (e) {
        recordError("notes/fee", e); // the exchange can't price it now: the dollars, if there are any
      }
    }
    return null;
  }
  const feeCalls = (f: FeePlan): NoteFeeCalls | undefined => (f && "calls" in f ? f.calls : undefined);
  /** A plan whose exchange quote is near its 60-second end is planned again before it is signed. */
  const feeStale = (f: FeePlan) => !!f && "voucher" in f && f.voucher && Date.now() - f.at > 45_000;
  function paintFee(f: FeePlan) {
    const el = container.querySelector<HTMLElement>("#srCashFee");
    if (!el) return;
    feeLine = !f ? "" : "sponsored" in f ? "Network fee — on us" : `Network fee — ${f.shown}`;
    el.textContent = feeLine;
    syncCashNote(); // 1.4.74 — the foot appears with the fee, not before
  }
  let cashFee: Promise<FeePlan> = Promise.resolve(null);
  function deriveOne(kind: SecretKind): Promise<CashSecret | null> {
    return (async () => {
      const s = newSecret(kind);
      const d = await derive(kdfInput(s.kind, s.secret), s.salt);
      recordMeasure("M® Note KDF", `${d.ms} ms (64 MiB, wasm, ${d.where})`); // the Lab line (brief §8.6); a measurement, not an error
      return { s, d };
    })().catch((e) => { recordError("notes/prepare", e); return null; });
  }
  /** v2.0 — the userOp for one variant, prepared once (Circle's stub only). */
  function ensurePrep(kind: SecretKind) {
    const sd = cashDerived[kind];
    if (!sd || cashPreps[kind]) return;
    const amount = cashAmount;
    cashPreps[kind] = sd.then(async (x) => {
      if (!x) return null;
      const fee = await cashFee;
      const calls = notes.issueCalls(amount, x.d.address, x.s.salt, cur(), { fee: feeCalls(fee) }); // 1.4.55: on the pocket's series; 1.4.71: with its fee
      const prepared = await adapter.prepare(calls).catch((e) => { console.warn("[M] prepare (cash) failed", e); return null; });
      return { ...x, calls, prepared, fee };
    }).catch((e) => { recordError("notes/prepare", e); return null; });
  }
  function prepareCash(amount: number) {
    cashAmount = amount;
    forgetCash();
    cashEnabled = issuingEnabled(notes.live);
    if (!notes.live) return; // not switched on in this build: the slide says so
    paintFee(null);
    cashFee = planFee(amount);
    void cashFee.then((f) => { if (cashAmount === amount) paintFee(f); });
    const code = deriveOne("code");
    cashDerived.code = code;
    if (amount < CASH_ALWAYS_LOCKED) cashDerived.link = code.then(() => deriveOne("link")); // one after the other
    ensurePrep(kindNow());
  }
  /** v2.0 — the finger lands on the thumb: the sponsor's signature for the variant shown, unless the
   *  slide will plan the note again anyway (a fee quote near its end — doCash). */
  function touchCash() {
    const p = cashPreps[kindNow()];
    if (p) sponsorOnTouch(adapter, p.then((prep) => (prep && !feeStale(prep.fee) ? prep.prepared : null)));
  }

  // v1.1.17 — a sent note's secret is forgotten only when it is CERTAIN
  // nothing was sent: a passkey cancel, a bundler that refused the userOp, or a
  // receipt with success false. Anything uncertain (the receipt wait timed out
  // — a phone locked after the slide —, the network dropped after the request
  // left) keeps the record: the userOp may be mined, and the secret on this
  // phone is the only way the note can ever be taken (cash session, T2032Z).
  // reconcileSent drops a record that never became a note, after reading Arc.
  async function doCash() {
    cashNoteGone = true; // 1.4.74 — the foot goes at the slide, for this card
    syncCashNote();
    const amount = cashAmount;
    if (!(amount > 0)) { slider.reset(); showStep("amount"); return; }
    if (!(await cashEnabled)) { cashNotLiveYet(); return; }
    const kind = kindNow();
    statusEl.textContent = "";
    ensurePrep(kind);
    const prep = await (cashPreps[kind] ?? Promise.resolve(null));
    if (!prep) {
      slider.reset();
      statusEl.textContent = "Couldn't get the note ready. Tap again in a moment.";
      delete cashPreps[kind];
      cashDerived[kind] = deriveOne(kind);
      ensurePrep(kind);
      return;
    }
    const { s, d } = prep;
    let { calls, prepared } = prep;
    if (feeStale(prep.fee)) {
      // 1.4.71 — the card sat past its last refresh: the fee's exchange quote is about to run out. A new one, and the plain path.
      cashFee = planFee(amount);
      calls = notes.issueCalls(amount, d.address, s.salt, cur(), { fee: feeCalls(await cashFee) });
      prepared = null;
    }
    // The secret is written down BEFORE the signature: a note whose secret
    // nobody kept could never be taken (notes.ts).
    keepSent({ controller: d.address, salt: s.salt, kind: s.kind, secret: s.secret, amount, currency: cur(), at: Date.now() }); // 1.4.55: the currency
    let sub: Submitted;
    try {
      const fresh = prepared && Date.now() - prepared.at < PREPARED_TTL_MS;
      // v2.0 — a nonce that moved since the prepare (another send) is "prepared_stale": the plain path, before any Face ID.
      sub = fresh
        ? await adapter.sendPrepared(prepared!).catch((e) => { if (e instanceof Error && e.message === "prepared_stale") return adapter.submit(calls); throw e; })
        : await adapter.submit(calls);
    } catch (e) {
      recordError("cash/send", e);
      if (isPasskeyCancel(e)) { forgetSent(d.address); closeOverlay(); slider.reset(); statusEl.textContent = PASSKEY_CANCEL_LINE; return; }
      forgetCash(); // the secret may be on its way: never offer it again
      if (lostInTransit(e)) { overlayCashPending(amount, s, d, false); return; }
      forgetSent(d.address); // the bundler answered, and refused it: nothing was sent
      overlayError(failureLine(e)); // 1.4.77 — the cause only when the refusal names it
      slider.reset();
      return;
    }
    // 1.5.31 — accepted: now the overlay (it waits for Arc's receipt, which carries the serial), not before the signature
    overlaySpinner(amount);
    const h = overlay.querySelector<HTMLElement>(".do-h");
    if (h) h.textContent = `Sending ${formatNoteAmount(cur(), amount)}…`; // 1.4.54: a note is in the pocket's currency
    forgetCash(); // a used secret is never offered again
    spendCashNote(); // v1.1.21 — the foot note's first three
    patchSent(d.address, { userOpHash: sub.userOpHash });
    void recordNote({ address, kind: "issue", amount, controller: d.address, userOpHash: sub.userOpHash });
    // The link is the serial, and the serial is assigned when Arc mines the
    // note — so this card waits for the receipt (brief §8.3), as Swap does.
    let rec: { txHash: string; success: boolean };
    try { rec = await sub.settled; } catch (e) {
      // No receipt is not a failed receipt: keep everything, tell the Worker
      // nothing (its resolver settles the row from the chain), and wait for Arc.
      recordError("cash/receipt", e);
      overlayCashPending(amount, s, d, false, sub.userOpHash);
      return;
    }
    if (!rec.success) {
      forgetSent(d.address);
      void recordNote({ address, kind: "issue", amount, controller: d.address, userOpHash: sub.userOpHash, txHash: rec.txHash || undefined, success: false });
      overlayError("Nothing moved. Try again in a moment.");
      slider.reset();
      return;
    }
    deps.onDropComplete(); // the balance went down
    const note = await settleSent(notes, d.address).catch(() => null);
    if (note) {
      void recordNote({ address, kind: "issue", amount, controller: d.address, userOpHash: sub.userOpHash, txHash: rec.txHash, success: true, serial: note.serial.text });
      overlayCashSent(note, s);
      return;
    }
    // Mined, but Arc's read hasn't caught up: the code can show now.
    void recordNote({ address, kind: "issue", amount, controller: d.address, userOpHash: sub.userOpHash, txHash: rec.txHash, success: true });
    overlayCashPending(amount, s, d, true, sub.userOpHash, rec.txHash);
  }

  /** A send error that says nothing about whether the bundler got the userOp. */
  function lostInTransit(e: unknown): boolean {
    let cur: unknown = e;
    for (let i = 0; cur && i < 8; i++) {
      const o = cur as { name?: unknown; message?: unknown; cause?: unknown };
      const n = typeof o.name === "string" ? o.name : "";
      const m = typeof o.message === "string" ? o.message : "";
      if (/^(HttpRequestError|TimeoutError|AbortError|WebSocketRequestError)$/.test(n)) return true;
      if (/Failed to fetch|Load failed|NetworkError|network connection was lost|timed out/i.test(m)) return true;
      cur = o.cause;
    }
    return false;
  }

  // v1.1.17 — the card while Arc catches up (Alexander, 2 Oct 21:54). Two cases:
  // MINTED (the receipt said success, only Arc's read of the serial is behind):
  // the note exists, so a code shows at once and the link's place says it is on
  // its way. NOT CONFIRMED (no receipt: the wait timed out or the network
  // dropped): the note may never exist, so no code — a code is never shown for
  // a note that failed. Either way the card keeps reading Arc while it is open
  // and becomes the sent card by itself when the note appears.
  let pendingTimer: ReturnType<typeof setTimeout> | null = null;
  function stopPending() {
    if (pendingTimer) clearTimeout(pendingTimer);
    pendingTimer = null;
  }
  function overlayCashPending(amount: number, s: NewSecret, d: Derived, minted: boolean, userOpHash?: string, txHash?: string) {
    stopPending();
    const locked = s.kind === "code";
    const code = locked ? formatCode(s.secret) : "";
    const showCode = minted && locked;
    slideState("moving"); // still on its way: the note shows, Arc hasn't confirmed
    overlay.innerHTML = `
      <div class="do-stack cash-done">
        <div class="do-card">
          <button id="srDoClose" class="do-close" type="button" aria-label="Close">${IC_CLOSE}</button>
          <div class="mnote-sm" aria-label="M® Note, ${escapeHtml(formatNoteAmount(cur(), amount))}, ${minted ? "sent" : "waiting for Arc"}">
            <div class="mn-top"><span class="mn-den"><span class="mn-t">M<sup class="mn-r">®</sup> NOTE</span><small class="mn-back">${escapeHtml(backingLine(cur()))}</small></span><span class="mn-amt">${escapeHtml(formatNoteAmount(cur(), amount))}</span></div>
            <div class="mn-meta"><span class="mn-serial">${minted ? "Serial on its way" : "Waiting for Arc"}</span><span>${minted ? "Sent" : "Sending"}</span></div>
          </div>
          <div class="cd-blk">
            <div class="cd-k">The link</div>
            <div class="cd-wait"><span class="spinner"></span>${minted ? "On its way from Arc." : "Arc hasn't confirmed this note yet."}</div>
          </div>
          ${showCode ? `
          <div class="cd-rule"></div>
          <div class="cd-blk">
            <div class="cd-k">The code</div>
            <div class="cd-v cd-code"><span class="cd-text">${escapeHtml(code)}</span><button class="cd-copy" id="cdCopyCode" type="button" aria-label="Copy the code">${IC_COPY}</button></div>
            <div class="cd-crack">$40 billion and 12,000 years to crack.</div>
          </div>
          <div class="cd-say">Share the link. Say the code.<br>Don't send them together.</div>` : `
          <div class="cd-say">${minted
            ? "The link shows here in a moment."
            : "Nothing to share until it does. This card updates by itself, and the note is kept on this phone."}</div>`}
          <div class="cd-done" id="cdDone" role="status" aria-live="polite"></div>
        </div>
      </div>`;
    overlay.removeAttribute("hidden");
    const done = overlay.querySelector<HTMLElement>("#cdDone")!;
    overlay.querySelector("#cdCopyCode")?.addEventListener("click", async (e) => {
      try { await navigator.clipboard.writeText(code); flashCopied(e.currentTarget as HTMLElement, "Code copied"); } catch { done.textContent = "Couldn't copy. Press and hold to select it."; setTimeout(() => { done.textContent = ""; }, 1600); }
    });
    overlay.querySelector("#srDoClose")!.addEventListener("click", () => { stopPending(); closeOverlay(); resetToAmount(); });
    // Keep reading Arc while the card is open: every 2 s for the first
    // minute, then every 5 s. The note appearing is the proof it was minted.
    const card = overlay.firstElementChild;
    const t0 = Date.now();
    const tick = async () => {
      pendingTimer = null;
      if (overlay.hidden || overlay.firstElementChild !== card) return; // closed or replaced
      const note = await settleSent(notes, d.address).catch(() => null);
      if (overlay.hidden || overlay.firstElementChild !== card) return;
      if (note) {
        void recordNote({ address, kind: "issue", amount, controller: d.address, userOpHash, txHash, success: true, serial: note.serial.text });
        if (!minted) deps.onDropComplete(); // the balance went down after all
        overlayCashSent(note, s);
        return;
      }
      pendingTimer = setTimeout(() => void tick(), Date.now() - t0 < 60_000 ? 2000 : 5000);
    };
    pendingTimer = setTimeout(() => void tick(), minted ? 800 : 2000);
  }

  /** v1.3.8 (Alexander, 7 Oct) — "Link copied" / "Code copied", large and bold, laid over the field that was copied, for a moment. */
  function flashCopied(from: HTMLElement, text: string) {
    const field = from.closest<HTMLElement>(".cd-v");
    if (!field) return;
    field.querySelector(".cd-flash")?.remove();
    const f = document.createElement("span");
    f.className = "cd-flash";
    f.setAttribute("role", "status");
    f.textContent = text;
    field.append(f);
    setTimeout(() => f.classList.add("out"), 1400);
    setTimeout(() => f.remove(), 1700);
  }

  /** After the slide (brief §2.3): the note small, the link and the code apart. */
  function overlayCashSent(note: NoteView, s: NewSecret) {
    const locked = s.kind === "code";
    const link = noteLink(LINK_ORIGIN, note.serial, locked ? undefined : s.secret);
    const shown = link.replace(/^https?:\/\//, "");
    const code = locked ? formatCode(s.secret) : "";
    resultUp = true; // 1.5.60 — the button under it is Done, never a second note
    slideState("landed");
    overlay.innerHTML = `
      <div class="do-stack cash-done">
        <div class="do-card">
          <button id="srDoClose" class="do-close" type="button" aria-label="Close">${IC_CLOSE}</button>
          <div class="mnote-sm" aria-label="M® Note, ${escapeHtml(formatNoteAmount(note.currency, note.value))}, serial ${escapeHtml(formatSerial(note.serial))}, sent">
            <div class="mn-top"><span class="mn-den"><span class="mn-t">M<sup class="mn-r">®</sup> NOTE</span><small class="mn-back">${escapeHtml(backingLine(note.currency))}</small></span><span class="mn-amt">${escapeHtml(formatNoteAmount(note.currency, note.value))}</span></div>
            <div class="mn-meta"><span class="mn-serial">${escapeHtml(formatSerial(note.serial))}</span><span>Sent</span></div>
          </div>
          <div class="cd-blk">
            <div class="cd-k">The link</div>
            <div class="cd-v cd-link"><span class="cd-text">${escapeHtml(shown)}</span><button class="cd-copy" id="cdCopyLink" type="button" aria-label="Copy the link">${IC_COPY}</button></div>
            <button class="cd-share" id="cdShare" type="button">Share</button>
          </div>
          ${locked ? `
          <div class="cd-rule"></div>
          <div class="cd-blk">
            <div class="cd-k">The code</div>
            <div class="cd-v cd-code"><span class="cd-text">${escapeHtml(code)}</span><button class="cd-copy" id="cdCopyCode" type="button" aria-label="Copy the code">${IC_COPY}</button></div>
            <div class="cd-crack">$40 billion and 12,000 years to crack.</div>
          </div>
          <div class="cd-say">Share the link. Say the code.<br>Don't send them together.</div>` : `
          <div class="cd-say warn">Like cash: anyone with the link can take it</div>`}
          <div class="cd-done" id="cdDone" role="status" aria-live="polite"></div>
        </div>
      </div>`;
    const done = overlay.querySelector<HTMLElement>("#cdDone")!;
    const say = (t: string) => { done.textContent = t; setTimeout(() => { if (done.textContent === t) done.textContent = ""; }, 1600); };
    const copy = async (text: string, what: string, from: HTMLElement) => {
      try { await navigator.clipboard.writeText(text); flashCopied(from, `${what} copied`); } catch { say("Couldn't copy. Press and hold to select it."); }
    };
    const linkBtn = overlay.querySelector<HTMLElement>("#cdCopyLink")!;
    linkBtn.addEventListener("click", () => void copy(link, "Link", linkBtn));
    const codeBtn = overlay.querySelector<HTMLElement>("#cdCopyCode");
    codeBtn?.addEventListener("click", () => void copy(code, "Code", codeBtn));
    // The share sheet carries the link only — never the code, never a title (brief §5.3).
    overlay.querySelector("#cdShare")!.addEventListener("click", () => void shareOrCopy({ url: link }).then((r) => { if (r === "copied") flashCopied(linkBtn, "Link copied"); }));
    overlay.querySelector("#srDoClose")!.addEventListener("click", () => { closeOverlay(); resetToAmount(); });
  }

  // The Sending card's ✕ and its amount: back to the keypad, the amount kept.
  $("#scClose").addEventListener("click", () => showStep("amount"));
  $("#pcAmtRow").addEventListener("click", () => showStep("amount"));

  // --- v0.90.29 — prepare before the slide ---------------------------------
  // The pass card is "here is what you're about to approve", so the amount and
  // the recipient are final when it paints. The round trips (nonce, gas,
  // paymaster, fees — ~2.5 s on the phone) run now, behind the reading of the
  // card; the slide's release only signs. Nothing is signed or submitted here,
  // so a discarded preparation costs a few reads. Rung 3 is left to the slide:
  // it needs a voucher priced at that moment.
  let prepSend: { key: string; p: Promise<PreparedOp | null> } | null = null;
  function prepareForCard() {
    const amount = amtNumber();
    if (!(amount > 0) || plan || !picked) { prepSend = null; return; }
    const c = cur();
    const key = `${picked.address}|${canonicalAmount(amount)}|${c}`;
    if (prepSend?.key === key) return;
    prepSend = { key, p: adapter.prepareTransfer(picked.address, canonicalAmount(amount), c).catch((e) => { console.warn("[M] prepare failed", e); return null; }) };
  }
  /** v2.0 — the finger on the thumb: sponsor the prepared send (not a rung-3 payment: the slide prices that). */
  function touchSend() {
    if (prepSend && !plan) sponsorOnTouch(adapter, prepSend.p);
  }
  /** The prepared send for this exact payment, or null (then the plain path runs). */
  async function preparedSendFor(to: string, canonical: string): Promise<PreparedOp | null> {
    const key = `${to}|${canonical}|${cur()}`;
    if (!prepSend || prepSend.key !== key) return null;
    return prepSend.p;
  }

  const reduceMotion = () => matchMedia("(prefers-reduced-motion: reduce)").matches;
  const RECENT_REFS = "m.recentRefs"; // this device only, like the held rests
  const recentRefs = (): string[] => {
    try {
      const v = JSON.parse(localStorage.getItem(RECENT_REFS) ?? "[]");
      return Array.isArray(v) ? v.filter((x) => typeof x === "string") : [];
    } catch { return []; }
  };
  function rememberRef(ref: string) {
    if (!ref) return;
    try {
      const all = [ref, ...recentRefs().filter((r) => r.toLowerCase() !== ref.toLowerCase())].slice(0, 8);
      localStorage.setItem(RECENT_REFS, JSON.stringify(all));
    } catch { /* storage blocked */ }
  }
  // --- Send details: recipient + slide -------------------------------------
  // iOS pans the whole page to "reveal" a focused input even when it's already
  // above the keyboard, which reads as a jump. While a text field is focused
  // the body is pinned (position: fixed) so there is nothing to pan. The lock
  // must be in place BEFORE iOS decides whether to pan (it decides at focus
  // time from the current layout), so pin on pointerdown as well. (v1.1.0 —
  // only Request's note field is left on the page; the To and Ref screens
  // lock in openEdit.)
  {
    const el = $<HTMLInputElement>("#srReqRef");
    el.addEventListener("pointerdown", () => lockViewport(true));
    el.addEventListener("focus", () => lockViewport(true));
    el.addEventListener("blur", () => lockViewport(false));
  }

  // Contact pool for search: named contacts (m.nick) ∪ Recents, de-duped by
  // address. Names from the nickname store, codes from Recents. All local/instant.
  // One person can carry several of YOUR names (v0.70.9): the nickname is the
  // one shown; Drop labels stay searchable ("Lucy" still finds "ATG 2").
  type Contact = { address: string; name?: string; names: string[]; code?: string };
  function contactsPool(): Contact[] {
    const byAddr = new Map<string, Contact>();
    for (const r of readRecent()) {
      byAddr.set(r.address.toLowerCase(), { address: r.address, names: [], code: r.display });
    }
    for (const n of listNicks()) {
      const k = n.address.toLowerCase();
      const e = byAddr.get(k) ?? { address: n.address, names: [] };
      e.name = n.name;
      e.names.unshift(n.name);
      byAddr.set(k, e);
    }
    // v0.70.12 — Drop labels not yet folded into the nickname (device-only
    // "also" names) stay searchable.
    for (const e of byAddr.values()) {
      for (const a of alsoNames(e.address)) {
        if (!e.names.some((x) => x.toLowerCase() === a.toLowerCase())) e.names.push(a);
        e.name ??= a;
      }
    }
    return [...byAddr.values()];
  }
  const nameIncludes = (c: Contact, q: string) => c.names.some((x) => x.toLowerCase().includes(q.toLowerCase()));
  // Display priority: nickname → Drop label → code (never a bare hex at rest).
  const displayOf = (c: { address: string; name?: string; code?: string }) =>
    c.name ?? contactLabel(c.address, c.code ?? null);

  // v1.0.0 — the naming convention used everywhere a person appears: your
  // name for them, then (their own name + emoji) in the muted colour. With no
  // name of yours, their name leads and only the emoji is in brackets.
  function whoHtml(addr: string, own: string | undefined, code?: string): string {
    const p = cachedProfile(addr);
    const key = p?.code || code || null;
    const em = key ? emojiFor(key, p?.emoji) : "";
    const mine = own && own !== code && own !== p?.name ? own : undefined;
    if (mine) {
      const inner = [p?.name ? escapeHtml(p.name) : "", em].filter(Boolean).join(" ");
      return `<span class="cap">${escapeHtml(mine)}</span>${inner ? ` <span class="ct-theirs">(${inner})</span>` : ""}`;
    }
    const head = p?.name || own || contactLabel(addr, code ?? null);
    return `<span class="cap">${escapeHtml(head)}</span>${em ? ` <span class="ct-theirs">(${em})</span>` : ""}`;
  }

  const slider = initSlideToConfirm($("#srSlide"), {
    onConfirm: () => {
      // 1.5.60 (Alexander's recordings of 4 Nov) — under a result card (Paid, the note made, the held card) the button is
      // Done: it closes the card and goes home. It had gone back to Send, live, with the same payee and amount: a second
      // tap paid again, and under the held card it paid the held payment at once, the Timelock skipped.
      if (resultUp) { closeOverlay(); resetToAmount(); slider.reset(); return; }
      // 1.5.60 — a payment that matches an open hold goes only as that hold's own Pay (holdPass): anything else shows the
      // hold again, never sends it beside it.
      const twin = card.classList.contains("cash") ? null : openHoldFor();
      if (twin) { slider.reset(); overlayHeld(twin); return; }
      sayOnSlider("Sending…"); void (card.classList.contains("cash") ? doCash() : tlHere ? doHold() : doSend()); // 1.5.59 — a payment the Timelock holds
    },
    onX: () => document.getElementById("dockX")?.click(), // 1.5.33 — the dock's ✕ squircle is now the button's left segment
    // v2.0 — the sponsor's signature, asked for when the finger lands (done by the slide's end).
    onTouch: () => { unlockSound(); return card.classList.contains("cash") ? touchCash() : tlHere ? undefined : touchSend(); }, // 1.5.9 — the slide is the tap iOS needs before a sound
  });

  // 1.5.31 (the pay page's rule of 1.5.30, carried to Sending — v2.4's handover §6 item 4): from the slide until the
  // bundler accepts, the slider says Sending… on the green — no sensor named (Face ID on a phone, Touch ID or a password
  // on a Mac) and no overlay: "Sending $2.00… Confirming on Circle Arc" had stood over the screen behind the passkey
  // sheet, before anything was signed. Accepted: a named payment's card, as before; an M® Note's "Confirming on Circle
  // Arc" and the travelling light (true then: it waits for Arc's receipt). Every reset of the slider puts its words back.
  function sayOnSlider(text: string | null) {
    const l = $("#srSlideLbl");
    l.textContent = text ?? (resultUp ? "Done" : tlHere ? `Send ${whenShort(tlHere.at, tlNow())}` : "Send"); // 1.5.59 — "Send on Thursday" for a payment the Timelock holds; 1.5.60 — Done under a result card
    l.style.opacity = "1";
    $("#srSlide").classList.toggle("saying", text !== null);
  }
  { const reset0 = slider.reset.bind(slider); slider.reset = () => { reset0(); sayOnSlider(null); }; }

  // --- confirming overlay (spinner → success card → ✕ home) ----------------
  const overlay = $("#srOverlay");
  /** 1.5.60 — a result card is up (Paid, the note made, the held card): the button under it is Done. */
  let resultUp = false;
  function closeOverlay() {
    stopPending();
    overlay.setAttribute("hidden", "");
    overlay.innerHTML = "";
    slideState("");
    if (resultUp) { resultUp = false; sayOnSlider(null); }
  }
  /** 1.5.8 (Alexander, 24 Oct) — the slider in the dock as money in motion: "moving" from the slide until Arc confirms (a
   *  light travelling across it, no words, no second spinner), "landed" when it has (a crisp tick); none on an error, a
   *  cancel or a close. CSS: .focus #srSlide.done.moving / .landed. */
  function slideState(st: "" | "moving" | "landed") {
    const el = $("#srSlide");
    if (st === "landed" && !el.classList.contains("landed")) playSent(); // 1.5.9 — M's sound with the tick, once
    el.classList.toggle("moving", st === "moving");
    el.classList.toggle("landed", st === "landed");
  }
  function overlaySpinner(amount: number) {
    slideState("moving");
    overlay.innerHTML = `
      <div class="do-body">
        <div class="spinner big"></div>
        <div class="do-h">Sending ${escapeHtml(fmt(amount))}…</div>
        <div class="do-s">Confirming on Circle Arc</div>
      </div>`;
    overlay.removeAttribute("hidden");
  }
  // v0.90.26 — shown at ACCEPTANCE (the bundler has the signed userOp), so
  // there is no receipt yet and the card doesn't pretend to one: the receipt
  // is in Recent transactions / Activity, where it will be looked at once.
  function overlaySuccess(
    amount: number,
    label: string,
    ref: string,
    to?: { address: string; code?: string },
    ccy: string = cur(),
  ) {
    // v1.1.24 — their name (yours for them, else theirs), on its own line under "sent to".
    const who = () => (to ? whoHtml(to.address, label, to.code) : `<span class="cap">${escapeHtml(label)}</span>`);
    // 1.5.60 — in the payment's own currency: the payment's done-callback has already put the default pocket back (a €2
    // payment read "$2.00 sent", Alexander's recordings of 4 Nov).
    const fmt = (n: number) => (ccy === "USD" ? formatUsdShort(n) : formatFx(ccy, n));
    resultUp = true;
    slideState("landed");
    overlay.removeAttribute("hidden"); // 1.5.35 — since 1.5.31 no spinner opens the overlay before the signature: the card opens it
    overlay.innerHTML = `
      <div class="do-stack">
        <div class="brand small">${LOGO_M}</div>
        <div class="do-card">
          <button id="srDoClose" class="do-close" type="button" aria-label="Close">✕</button>
          <div class="tickbig">✓</div>
          <div class="do-h one"><span class="amt">${escapeHtml(fmt(amount))}</span> sent to <span class="nm" id="srDoWho">${who()}</span></div>
          ${ref ? `<div class="do-ref"><span class="pc-k">Ref:</span> ${escapeHtml(ref)}</div>` : ""}
          <div class="do-s muted">Receipt in Recent transactions.</div>
        </div>
      </div>`;
    overlay.querySelector("#srDoClose")!.addEventListener("click", () => {
      closeOverlay();
      resetToAmount();
    });
    if (to) void warmProfiles([to.address]).then(() => { const el = overlay.querySelector("#srDoWho"); if (el) el.innerHTML = who(); });
  }
  // 1.4.77 (v1.9's note on Alexander's video, 22 Oct) — as the swap's (1.4.24): no ✕ (Back does the same, and the title
  // had run under it: "…ayment didn't go through"), the title fitted to one line; the cause only when the refusal names it.
  function overlayError(msg: string) {
    slideState("");
    overlay.removeAttribute("hidden"); // 1.5.35 — as overlaySuccess: nothing else opens it now
    overlay.innerHTML = `
      <div class="do-body">
        <div class="do-h pay-fail">Payment didn't go through</div>
        <div class="do-s">${escapeHtml(msg)}</div>
        <button id="srDoBack" class="btn ghost" type="button" style="margin-top:14px">Back</button>
      </div>`;
    fitFailTitle(overlay);
    overlay.querySelector("#srDoBack")!.addEventListener("click", closeOverlay);
  }

  function resetToAmount() {
    if (document.activeElement instanceof HTMLElement) document.activeElement.blur(); // v1.0.12 — keyboard down first
    pillSum = 0;
    typed = "";
    touched = false;
    over = false;
    deps.onOver?.(false);
    picked = null;
    $<HTMLInputElement>("#srRef").value = "";
    $<HTMLInputElement>("#srReqRef").value = "";
    showStep("amount");
  }

  // ---- 1.5.59 — the Timelock's stand-in (claude/timelock.md §6, §8; timelock.ts) -------------------------------------
  // A payment that would empty its pocket isn't sent: said before Face ID ("It can go at …", the button "Send on
  // Thursday"), the tap makes the hold (no Face ID — nothing is signed), and the held card shows. When the Worker says it
  // is due, the home's line says it's ready, with Pay: this card again, the hold's own payment let through (holdPass).
  /** This card's payment is one the Timelock holds: when it could go. */
  let tlHere: { at: number } | null = null;
  /** Paying a due hold: its payment goes through the card unheld, once. */
  let holdPass: { id: string; to: string; amount: string } | null = null;
  const holdAmt = (code: string, n: number) => (code === "USD" ? formatUsdShort(n) : formatFx(code, n)).replace(/\.00$/, "");
  function holdName(addr: string): string {
    const a = addr.toLowerCase();
    return listNicks().find((n) => n.address.toLowerCase() === a)?.name ?? cachedProfile(addr)?.name ?? contactLabel(addr, null);
  }
  function paintTimelock() {
    tlHere = null;
    const v = tlView();
    const who = picked;
    if (!v?.policy || v.policy.posture === "off" || !who) return;
    const amount = amtNumber();
    if (holdPass && holdPass.to === who.address.toLowerCase() && holdPass.amount === canonicalAmount(amount)) return; // the hold's own payment, due
    if (!wouldHold(amount, cur(), v.figures)) return; // 1.5.60: every payment over the owner's amount
    tlHere = { at: tlNow() + v.figures.H2 };
    sayOnSlider(null);
    statusEl.innerHTML = `<span class="tl-say"><span class="tl-g warn" aria-hidden="true">${IC_TIMELOCK}</span>It can go at ${escapeHtml(whenText(tlHere.at, tlNow()))}.</span>`;
  }
  async function doHold() {
    const to = picked;
    const amount = amtNumber();
    const tok = currency(cur()).token;
    if (!to || !(amount > 0) || !tok) { slider.reset(); return; }
    const refDisp = $<HTMLInputElement>("#srRef").value.replace(/\s+/g, " ").trim().slice(0, 40);
    try {
      const h = await holdThis(address, { token: tok.address, amount: parseUnits(canonicalAmount(amount), tok.decimals).toString(), to: to.address, ref: refDisp || undefined });
      tlHere = null; // held: the dock's button no longer offers it
      statusEl.textContent = "";
      overlayHeld(h);
    } catch (e) {
      recordError("timelock/hold", e);
      overlayError(failureLine(e));
    } finally {
      slider.reset();
    }
  }
  /** The held card, where the Paid card stands: the glyph in its ring, what and to whom, when it can go — and the
   *  posture's way to go sooner (Faster release: the Release Word — 1.5.60, the codeword until then), then Cancel.
   *  Ready: Pay. The word is typed in the clear (Alexander, 4 Nov: masking adds mistakes and doesn't stop a hidden camera);
   *  capitals and spaces don't count (normalWord). */
  function overlayHeld(h: TimelockHold, asking = false) {
    const v = tlView();
    const posture = v?.policy?.posture ?? "nobody";
    const { code, amount } = holdMoney(h);
    const now = tlNow();
    const ph = phaseOf(h, now);
    const line = ph === "held" ? `It can go at ${whenText(h.goesAt, now)}.`
      : ph === "released" ? `Released. It can go at ${whenText(h.goesAt, now)}.`
      : ph === "ready" ? "It’s ready to go."
      : ph === "confirming" ? "Confirming on Circle Arc…" : "Circle Arc hasn’t confirmed it yet.";
    // 1.5.60 — no second release while one runs (Alexander: "Release is still an available option after… release"; the
    // right word again moved the time on by R2). Past its time and not due, it is offered again: the owner who mistyped.
    const canRelease = posture === "word" && (ph === "held" || ph === "confirming" || ph === "stuck");
    resultUp = true;
    slideState("landed");
    overlay.removeAttribute("hidden");
    overlay.innerHTML = `
      <div class="do-stack">
        <div class="brand small">${LOGO_M}</div>
        <div class="do-card tl">
          <button id="srDoClose" class="do-close" type="button" aria-label="Close">✕</button>
          <div class="tl-big${ph === "held" ? "" : " rd"}" aria-hidden="true">${ph === "held" ? IC_TIMELOCK : IC_RELEASE_DELAY}</div>
          <div class="do-h one"><span class="amt">${escapeHtml(holdAmt(code, amount))}</span> to <span class="nm">${escapeHtml(holdName(h.to))}</span>${ph === "ready" ? "" : " is on hold"}</div>
          ${h.ref ? `<div class="do-ref"><span class="pc-k">Ref:</span> ${escapeHtml(h.ref)}</div>` : ""}
          <div class="do-s" id="tlLine">${escapeHtml(line)}</div>
          ${posture === "nobody" && ph === "held" ? `<div class="do-s muted">Nobody can make this one sooner.</div>` : ""}
        </div>
        <div class="tl-acts" id="tlActs">
          ${ph === "ready" ? `<button class="btn primary block" id="tlPay" type="button">Send</button>` : ""}
          ${canRelease && !asking ? `<button class="btn ghost block" id="tlRelease" type="button">Release early…</button>` : ""}
          ${canRelease && asking ? `<form class="tl-ask" id="tlAsk"><input class="tl-field" id="tlWord" type="text" autocomplete="off" autocorrect="off" autocapitalize="none" spellcheck="false" enterkeyhint="go" aria-label="Release Word" placeholder="Release Word"><button class="btn primary block" id="tlGo" type="submit">Release</button></form>` : ""}
          <button class="linkish small" id="tlCancel" type="button">Cancel this payment</button>
        </div>
      </div>`;
    const close = () => { closeOverlay(); resetToAmount(); };
    overlay.querySelector("#srDoClose")!.addEventListener("click", close);
    overlay.querySelector("#tlCancel")!.addEventListener("click", () => {
      void cancelThis(address, h).then(close, (e) => { recordError("timelock/cancel", e); toastLine("Couldn’t reach M. Try again."); });
    });
    overlay.querySelector("#tlPay")?.addEventListener("click", () => { closeOverlay(); payHold(h); });
    overlay.querySelector("#tlRelease")?.addEventListener("click", () => { overlayHeld(h, true); overlay.querySelector<HTMLInputElement>("#tlWord")?.focus(); });
    const form = overlay.querySelector<HTMLFormElement>("#tlAsk");
    form?.addEventListener("submit", (e) => {
      e.preventDefault();
      const word = overlay.querySelector<HTMLInputElement>("#tlWord")!.value;
      const salt = tlView()?.policy?.salt;
      if (!word.trim() || !salt) return;
      const go = overlay.querySelector<HTMLButtonElement>("#tlGo")!;
      go.disabled = true;
      go.textContent = "Releasing…";
      // No count of tries and no "wrong" — ever: the Release Word, the Lock Word and a wrong word get the same line.
      void tlRelease(address, h, word, salt as `0x${string}`).then(
        (goesAt) => overlayHeld({ ...h, releasedAt: tlNow(), goesAt, due: false }),
        (e) => { recordError("timelock/release", e); go.disabled = false; go.textContent = "Release"; toastLine("Couldn’t reach M. Try again."); },
      );
    });
  }
  function toastLine(t: string) { const el = overlay.querySelector<HTMLElement>("#tlLine"); if (el) el.textContent = t; }
  /** 1.5.60 — the open hold this card's payment is (same payee, amount and token), unless it is that hold's own Pay. */
  function openHoldFor(): TimelockHold | null {
    const who = picked;
    const tok = currency(cur()).token;
    const v = tlView();
    if (!who || !tok || !v?.holds.length) return null;
    const amount = amtNumber();
    if (!(amount > 0)) return null;
    const units = parseUnits(canonicalAmount(amount), tok.decimals).toString();
    const h = v.holds.find((x) => x.to.toLowerCase() === who.address.toLowerCase() && x.token.toLowerCase() === tok.address.toLowerCase() && x.amount === units);
    if (!h) return null;
    if (holdPass && holdPass.id === h.id && h.due) return null; // its own Pay, due: let it through
    return h;
  }
  /** A due hold: this card again, with its payee and amount, the hold's own payment let through. */
  function payHold(h: TimelockHold) {
    const { code, amount } = holdMoney(h);
    if (code !== cur()) setSessionCurrency(code);
    holdPass = { id: h.id, to: h.to.toLowerCase(), amount: canonicalAmount(amount) };
    applyPrefill({ toAddress: getAddress(h.to), toDisplay: holdName(h.to), amount, reference: h.ref ?? undefined });
  }
  /** The home's line for the hold it shows (paintSay). */
  function timelockSay(): { text: string; chips: Chip[] } | null {
    const h = tlPrimary();
    if (!h) return null;
    const now = tlNow();
    const { code, amount } = holdMoney(h);
    const what = `${holdAmt(code, amount)} to ${holdName(h.to)}`;
    const cancel: Chip = { label: "Cancel", tap: () => void cancelThis(address, h).catch((e) => recordError("timelock/cancel", e)) };
    switch (phaseOf(h, now)) {
      case "held": return { text: `${what} is on hold until ${whenText(h.goesAt, now)}.`, chips: [cancel] };
      case "released": return { text: `Released. ${what} can go at ${whenText(h.goesAt, now)}.`, chips: [] }; // 1.5.60 — says which
      case "confirming": return { text: "Confirming on Circle Arc…", chips: [] };
      case "stuck": return { text: "Circle Arc hasn’t confirmed it yet.", chips: [] };
      case "ready": return { text: `${what} is ready.`, chips: [{ ...cancel, cls: "quiet" }, { label: "Send", tap: () => payHold(h) }] }; // 1.5.60 (Alexander): Cancel | Send, the same width
    }
  }
  {
    const off = onTimelock(() => {
      if (!container.isConnected) { off(); return; }
      if (!$("#srAmount").hidden) paintSay();
    });
  }

  /** A payment to the chosen contact (the card's slide when it isn't M® Note). */
  async function doSend() {
    const amount = amtNumber();
    const to = picked;
    const paidIn = cur(); // 1.5.60 — the Paid card's currency (the done-callback puts the default pocket back first)
    if (!(amount > 0) || !to) { // the card only offers this slide with an amount and a contact
      slider.reset();
      showStep("amount");
      return;
    }
    statusEl.textContent = "";
    const canonical = canonicalAmount(amount);
    // v1.0.4 — a Send's note is not a link: keep it as typed (the link
    // grammar's slug stripped punctuation — "Lunch @ Dave's!" → "Lunch Daves").
    const refDisp = $<HTMLInputElement>("#srRef").value.replace(/\s+/g, " ").trim().slice(0, 40);
    // 1.5.31 — no overlay before the signature: the slider says Sending…; the card comes at acceptance
    let topUp: FxTopUp | null = null;
    try {
      // v0.90.19 — with a shortfall: approve(MFx) + swap + transfer in ONE
      // userOp, one Face ID. Without: the plain transfer, exactly as before.
      topUp = await topUpFor();
      // v0.90.26 — success at acceptance: Face ID, then the bundler takes the
      // userOp and the card shows. The receipt is followed in the background
      // (bookkeeping below) — and if this tab dies first, the Worker resolves
      // the submitted row from the chain (pending.ts).
      let sub: Submitted | null = null;
      if (!topUp) {
        // v0.90.29 — prepared while the card was up: Face ID at once.
        const p = await preparedSendFor(to.address, canonical);
        if (p) {
          try {
            sub = await adapter.sendPrepared(p);
          } catch (e) {
            if (isPasskeyCancel(e)) throw e;
            if (!(e instanceof Error && e.message === "prepared_stale")) recordError("send/prepared", e); // refused → the plain path below
            sub = null;
          }
        }
      }
      if (sub) {
        /* prepared and accepted */
      } else if (topUp) {
        const tok = currency(cur()).token!;
        sub = await adapter.submit([
          ...topUp.calls,
          {
            to: tok.address,
            data: encodeFunctionData({
              abi: ERC20_TRANSFER_ABI,
              functionName: "transfer",
              args: [to.address as `0x${string}`, parseUnits(canonical, tok.decimals)],
            }),
          },
        ]);
      } else {
        sub = await adapter.submitTransfer(to.address, canonical, cur());
      }
      const base = { address, userOpHash: sub.userOpHash, direction: "send" as const, amount: canonical, counterparty: to.address, currency: cur(), kind: "send" as const, ref: refDisp || undefined }; // v1.0.4 — the note reaches the statement
      recordTx(base).catch((e) => console.warn("[M] tx/record (submit) failed:", e));
      // 1.5.59 — the hold's own payment, accepted: the Worker closes the hold (at acceptance, so a dead tab doesn't offer it again)
      if (holdPass && holdPass.to === to.address.toLowerCase() && holdPass.amount === canonical) { const id = holdPass.id; holdPass = null; void paidThis(address, id, sub.userOpHash).catch((e) => recordError("timelock/sent", e)); }
      addRecent({ address: to.address, display: to.code ?? to.label });
      deps.onDropComplete();
      overlaySuccess(amount, to.label, refDisp, { address: to.address, code: to.code }, paidIn);
      rememberRef(refDisp); // v1.0.13 — the note screen offers it next time
      const tu = topUp;
      void sub.settled
        .then(({ txHash, success }) => {
          recordTx({ ...base, txHash, success }).catch((e) => console.warn("[M] tx/record (receipt) failed:", e));
          if (!success) { recordError("send/settle", new Error("userop_reverted " + txHash)); return; }
          addTxLog({ txHash, to: to.address, code: to.code, amount: canonical, ref: refDisp || undefined }); // v0.60.4
          if (tu) deps.fx?.reportTopUp?.(tu, txHash, true);
          deps.onDropComplete(); // a second refresh once the leg is on chain
        })
        .catch((e) => recordError("send/settle", e));
    } catch (e) {
      console.error(e);
      if (e instanceof Error && e.message === "rate_moved") {
        closeOverlay();
        statusEl.textContent = "The rate moved — the card shows the new figure. Tap again.";
        return;
      }
      recordError("send", e); // v0.80.11 — full cause chain → Lab
      if (isPasskeyCancel(e)) { // v0.90.22 — a cancel re-arms the slide, no card
        closeOverlay();
        statusEl.textContent = PASSKEY_CANCEL_LINE;
        return;
      }
      // The Worker's refusals are sentences (FxRefused); anything else is generic.
      overlayError(e instanceof Error && e.name === "FxRefused" ? e.message : failureLine(e)); // 1.4.77 — no guessed cause
    } finally {
      slider.reset();
    }
  }

  // --- Receive details: share card (QR + link + Copy/Share) ----------------
  function buildRequestUrl(): { url: string; refDisp: string } | null {
    if (!name?.slug || !isWalletCode(name.slug)) return null;
    const refInput = $<HTMLInputElement>("#srReqRef").value;
    const url = requestUrl(LINK_ORIGIN, name.slug, amtNumber(), refInput || undefined, cur());
    const refSlug = refInput ? slugifyRef(refInput) : "";
    return { url, refDisp: refSlug ? deslugifyRef(refSlug) : "" };
  }

  // Reference field (Get paid): re-slugify live into the link + QR.
  $<HTMLInputElement>("#srReqRef").addEventListener("input", () => {
    if (mode !== "receive" || gpStage !== "link") return;
    const b = buildRequestUrl();
    if (!b) return;
    $("#srGpLinkText").textContent = b.url.replace(/^https?:\/\//, "");
    $("#srGpReqQr").innerHTML = qrBox(b.url);
  });
  $<HTMLInputElement>("#srReqRef").addEventListener("keydown", (e) => {
    if (e.key === "Enter" && !e.isComposing) { e.preventDefault(); (e.target as HTMLInputElement).blur(); }
  });

  // --- initial paint -------------------------------------------------------
  renderAmt();
  void refreshLadder(); // v0.90.19
  void refreshUntaken(); // v1.1.21 — a note nobody has taken yet gets a line

  // Pre-fill from a pay link ("Go to my wallet"): lock in the recipient, and if
  // the amount came through, open straight on the Send details step.
  function applyPrefill(pf: SendPrefill) {
    mode = "send";
    picked = { address: pf.toAddress, label: pf.toDisplay, code: pf.toDisplay };
    $<HTMLInputElement>("#srRef").value = pf.reference ?? "";
    deps.onMode?.("send");
    if (pf.amount && pf.amount > 0) {
      setAmt(0, canonicalAmount(pf.amount));
      enterDetails();
    } else if (hasAmt() && !$("#srDetails").hidden) {
      enterDetails(); // v1.1.21 — scanned from the card: the amount stays, their name is in the pill
    } else {
      setAmt(0, "");
      showStep("amount");
    }
  }

  if (deps.prefill) {
    applyPrefill(deps.prefill);
  }

  return {
    openHold: () => { const h = tlPrimary(); if (!h) return; deps.onCard?.(true); overlayHeld(h); }, // 1.5.59; 1.5.60 — the dock steps aside (it stayed live under the card)
    refreshAvailable: () => {
      // A balance read or a pocket switch: does the amount still fit? The
      // pill's symbol follows the pocket (v0.80.32).
      over = hasAmt() && isOver(amtNumber());
      deps.onOver?.(over);
      if (!$("#srAmount").hidden) renderAmt();
      void refreshLadder().then(() => {
        // the ceiling may have moved with the pocket: re-check a typed amount
        if (!hasAmt()) return;
        over = isOver(amtNumber());
        deps.onOver?.(over);
        if (!$("#srAmount").hidden) renderAmt();
      });
    },
    backToAmount: () => showStep("amount"),
    home: () => {
      // v1.0.10 — the dock centre is a fresh start: amount, recipient and note
      // all go. v1.1.21 — and it is the way back to sending from Get paid.
      home();
    },
    openSend: (pf: SendPrefill) => applyPrefill(pf),
    open: () => {
      pillSum = 0;
      typed = "";
      touched = false;
      picked = null;
      void refreshLadder();
      void refreshUntaken();
      setMode("send");
      showStep("amount");
    },
    setMode: (m) => setMode(m),
    go: goNext,
    clear: clearAmt,
    mode: () => mode,
    setGlobal: (on) => {
      const lit = on && !global;
      global = on;
      // 1.4.15 — tapping the globe dismisses the home's opening line (the greeting), so the amount shows.
      if (lit) {
        if (typeof deps.welcomeMoney?.() === "number" || deps.welcomeNote?.()) welcomeSaid = true;
        welcomeAsk = false;
      }
      if (!$("#srAmount").hidden) renderAmt();
      else paintPockets();
    },
    repaint: () => {
      if (!$("#srAmount").hidden) paintSay();
    },
  };
}
